import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecording } from '../RecordingContext';
import ReactFlow, {
  Background, Controls, MiniMap, Handle, Position,
  useNodesState, useEdgesState, addEdge, MarkerType, NodeResizer, NodeResizeControl,
} from 'reactflow';
import dagre from '@dagrejs/dagre';
import 'reactflow/dist/style.css';
import {
  Box, Typography, IconButton, Stack, Button, Drawer, TextField,
  Select, MenuItem, FormControl, InputLabel, Divider, Tooltip,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, Alert,
  Autocomplete, useMediaQuery, Snackbar, LinearProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ViewListIcon from '@mui/icons-material/ViewList';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArticleIcon from '@mui/icons-material/Article';
import MicIcon from '@mui/icons-material/Mic';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RecordingModal from '../components/RecordingModal';
import { Popover } from '@mui/material';
import { Collapse } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../api';

// Shared markdown styles for AI-generated content
const aiMdSx = {
  '& p': { m: 0, mb: 1, fontSize: '0.88rem', color: 'var(--tx-lo)', lineHeight: 1.75 },
  '& strong': { color: 'var(--tx-hi)', fontWeight: 700 },
  '& ul, & ol': { pl: 2.5, m: 0, mb: 1 },
  '& li': { fontSize: '0.85rem', color: 'var(--tx-lo)', lineHeight: 1.7, mb: 0.3 },
  '& h1, & h2, & h3': { fontSize: '0.9rem', fontWeight: 700, color: 'var(--tx-hi)', mt: 1.5, mb: 0.5 },
  '& code': { fontSize: '0.82rem', bgcolor: 'var(--bg-card)', px: 0.5, borderRadius: 0.5, fontFamily: 'monospace' },
  '& blockquote': { borderLeft: '3px solid #6366f155', pl: 1.5, my: 1, color: 'var(--tx-ft)', fontStyle: 'italic' },
  '& hr': { border: 'none', borderTop: '1px solid var(--bd-lo)', my: 1.5 },
};

// ─── Constants ────────────────────────────────────────────────────────────────
// Two parallel tracks:
//   Initiative → Opportunity → Activity → Task → Artifact
//   Project    → Workstream  → Activity → Task → Artifact
// Top-level (can be root): Initiative, Project, Opportunity*, Workstream*
// (*) Opportunity / Workstream may also be standalone roots
// Must always have a parent: Activity, Task, Artifact

const NODE_TYPES_ORDER = ['initiative', 'project', 'opportunity', 'workstream', 'activity', 'task', 'milestone', 'artifact'];

const NODE_META = {
  initiative:  { label: 'Initiative',  color: '#6366f1', bg: '#1e1b4b', border: '#4f46e5' },
  project:     { label: 'Project',     color: '#06b6d4', bg: '#071c24', border: '#0891b2' },
  opportunity: { label: 'Opportunity', color: '#f59e0b', bg: '#1c1008', border: '#d97706' },
  workstream:  { label: 'Workstream',  color: '#a78bfa', bg: '#1a1030', border: '#7c3aed' },
  activity:    { label: 'Activity',    color: '#10b981', bg: '#052e16', border: '#059669' },
  task:        { label: 'Task',        color: '#3b82f6', bg: '#0c1a2e', border: '#2563eb' },
  milestone:   { label: 'Milestone',   color: '#eab308', bg: '#1a1500', border: '#ca8a04' },
  pivot:       { label: 'Milestone',   color: '#eab308', bg: '#1a1500', border: '#ca8a04' }, // legacy alias
  artifact:    { label: 'Artifact',    color: '#f43f5e', bg: '#2d0a14', border: '#e11d48' },
};

const STATUS_META = {
  open:        { label: 'Open',        color: 'var(--tx-dim)' },
  in_progress: { label: 'In Progress', color: '#f59e0b' },
  done:        { label: 'Done',        color: '#10b981' },
  cancelled:   { label: 'Cancelled',   color: '#ef4444' },
  blocked:     { label: 'Blocked',     color: '#f97316' },
  suggested:   { label: 'Suggested',   color: '#a78bfa' },
};

const EDGE_META = {
  parent_of: { label: 'Parent of',  style: { stroke: 'var(--tx-gh)', strokeWidth: 2 },        markerEnd: { type: MarkerType.ArrowClosed, color: 'var(--tx-gh)' }, animated: false },
  leads_to:  { label: 'Leads to',   style: { stroke: '#6366f1', strokeWidth: 2, strokeDasharray: '6,3' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#6366f1' }, animated: true },
  follow_up: { label: 'Follow-up',  style: { stroke: '#10b981', strokeWidth: 1.5, strokeDasharray: '3,3' }, markerEnd: { type: MarkerType.ArrowClosed, color: '#10b981' }, animated: false },
};

// Milestone sub-types
const MILESTONE_SUBTYPES = [
  { value: 'pivot',    label: 'Pivot',    color: '#fb923c' },
  { value: 'signal',   label: 'Signal',   color: '#38bdf8' },
  { value: 'learning', label: 'Learning', color: '#c084fc' },
  { value: 'strategy', label: 'Strategy', color: '#f97316' },
  { value: 'outcome',  label: 'Outcome',  color: '#10b981' },
];

// Node types that support a goal field
const GOAL_TYPES = ['initiative', 'project', 'opportunity', 'workstream'];
// Node types that can trigger AI strategy suggestion
const STRATEGY_TYPES = ['initiative', 'project', 'workstream'];

// Which child types are allowed under each parent type
const ALLOWED_CHILDREN = {
  initiative:  ['opportunity', 'milestone'],
  project:     ['workstream', 'milestone'],
  opportunity: ['activity', 'milestone'],
  workstream:  ['activity', 'task', 'milestone'],
  activity:    ['task', 'activity', 'milestone'],
  task:        ['artifact', 'task', 'activity', 'milestone'],
  milestone:   ['activity', 'task', 'milestone'],
  pivot:       ['activity', 'task', 'milestone'], // legacy
  artifact:    [],
};

// Node types that can be created without a parent
const ROOT_TYPES = ['initiative', 'project', 'opportunity', 'workstream'];

// ─── Dagre layout ─────────────────────────────────────────────────────────────
const NODE_W = 240;
const NODE_H = 180; // generous estimate — nodes vary in height

function dagreLayout(nodes, edges) {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'LR', ranksep: 90, nodesep: 30 });
  nodes.forEach(n => g.setNode(n.id, { width: NODE_W, height: NODE_H }));
  edges.forEach(e => { if (g.node(e.source) && g.node(e.target)) g.setEdge(e.source, e.target); });
  dagre.layout(g);
  return nodes.map(n => {
    const pos = g.node(n.id);
    return { ...n, position: { x: pos.x - NODE_W / 2, y: pos.y - NODE_H / 2 } };
  });
}

// Swim-lane layout: header nodes (project/initiative/etc.) get their own section;
// each workstream + its descendants forms a horizontal lane stacked vertically.
function getLayoutedElements(nodes, edges, workstreamGroups) {
  const LANE_GAP = 60;       // vertical gap between swim lanes
  const LANE_PAD = 20;       // padding inside the lane background rect
  const HEADER_GAP = 80;     // gap between header section and first lane

  // No workstreams → plain layout
  if (!workstreamGroups || workstreamGroups.length <= 1) {
    return dagreLayout(nodes, edges);
  }

  const allLaneIds = new Set(workstreamGroups.flatMap(g => [...g.nodeIds]));
  const headerNodes = nodes.filter(n => !allLaneIds.has(n.id));
  const headerEdges = edges.filter(e => !allLaneIds.has(e.source) && !allLaneIds.has(e.target));

  // Layout each lane independently
  const laneResults = workstreamGroups.map(({ wsId, wsLabel, wsColor, wsStatus, nodeIds }) => {
    const laneNodes = nodes.filter(n => nodeIds.has(n.id));
    const laneEdges = edges.filter(e => nodeIds.has(e.source) && nodeIds.has(e.target));
    const laid = dagreLayout(laneNodes, laneEdges);
    // Bounding box of this lane
    const minY = Math.min(...laid.map(n => n.position.y));
    const maxY = Math.max(...laid.map(n => n.position.y + NODE_H));
    const minX = Math.min(...laid.map(n => n.position.x));
    const maxX = Math.max(...laid.map(n => n.position.x + NODE_W));
    return { wsId, wsLabel, wsColor, wsStatus, laid, minY, maxY, minX, maxX, height: maxY - minY, width: maxX - minX };
  });

  // Layout header nodes (vertically centred relative to all lanes)
  let headerWidth = 0;
  let headerLaid = [];
  if (headerNodes.length > 0) {
    headerLaid = dagreLayout(headerNodes, headerEdges);
    headerWidth = Math.max(...headerLaid.map(n => n.position.x + NODE_W));
  }

  // Stack lanes top-to-bottom, horizontally after header
  const laneXOffset = headerWidth > 0 ? headerWidth + HEADER_GAP : 0;
  let currentY = 0;
  const positionedLanes = [];
  const laneBackgrounds = [];

  for (const lane of laneResults) {
    const yShift = currentY - lane.minY;
    const xShift = laneXOffset - lane.minX;
    const shiftedNodes = lane.laid.map(n => ({
      ...n, position: { x: n.position.x + xShift, y: n.position.y + yShift },
    }));
    positionedLanes.push(...shiftedNodes);

    // Lane background node (rendered behind content nodes)
    laneBackgrounds.push({
      id: `lane_bg_${lane.wsId}`,
      type: 'laneBackground',
      position: { x: laneXOffset - LANE_PAD, y: currentY - LANE_PAD },
      data: { label: lane.wsLabel, color: lane.wsColor, isDone: lane.wsStatus === 'done', width: lane.width + LANE_PAD * 2, height: lane.height + LANE_PAD * 2 },
      draggable: false, selectable: false, focusable: false,
    });

    currentY += lane.height + LANE_GAP;
  }

  // Vertically centre header nodes against all lanes
  const totalLaneHeight = currentY - LANE_GAP;
  const headerShiftedNodes = headerLaid.map(n => ({
    ...n, position: { x: n.position.x, y: n.position.y + totalLaneHeight / 2 - (Math.max(...headerLaid.map(h => h.position.y + NODE_H)) / 2) },
  }));

  // Lane backgrounds must come first so they render behind content nodes
  return [...laneBackgrounds, ...headerShiftedNodes, ...positionedLanes];
}

// ─── Timeline layout ──────────────────────────────────────────────────────────
const TIMELINE_PX_PER_DAY = 120;

function applyTimelineLayout(rfNodes) {
  const trackerNodes = rfNodes.filter(n => n.type === 'trackerNode');
  const datedNodes = trackerNodes.filter(n => n.data?.target_date);
  if (datedNodes.length === 0) return rfNodes; // no dates — fall back to dagre

  const dateMs = datedNodes.map(n => new Date(n.data.target_date + 'T00:00:00').getTime());
  const minDateMs = Math.min(...dateMs);
  const maxDateMs = Math.max(...dateMs);

  // Reposition: X = date-based, Y = preserved from dagre
  const positioned = rfNodes.map(n => {
    if (n.type !== 'trackerNode') return { ...n, hidden: true }; // hide swim-lane backgrounds

    const targetDate = n.data?.target_date;
    let x;
    if (targetDate) {
      const dayOffset = (new Date(targetDate + 'T00:00:00').getTime() - minDateMs) / 86400000;
      x = dayOffset * TIMELINE_PX_PER_DAY;
    } else {
      x = -(NODE_W + 160); // undated nodes cluster off to the left
    }
    return { ...n, position: { x, y: n.position.y } };
  });

  // Month ruler nodes
  const minDate = new Date(minDateMs); minDate.setDate(1);
  const maxDate = new Date(maxDateMs); maxDate.setMonth(maxDate.getMonth() + 2); maxDate.setDate(1);
  const rulers = [];
  const cur = new Date(minDate);
  while (cur <= maxDate) {
    const dayOffset = (cur.getTime() - minDateMs) / 86400000;
    rulers.push({
      id: `tl_ruler_${cur.getTime()}`,
      type: 'timelineRuler',
      position: { x: dayOffset * TIMELINE_PX_PER_DAY, y: -90 },
      data: { label: cur.toLocaleDateString('en-SG', { month: 'short', year: '2-digit' }), isToday: false },
      draggable: false, selectable: false, focusable: false,
    });
    cur.setMonth(cur.getMonth() + 1);
  }

  // Today marker
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayOffset = (today.getTime() - minDateMs) / 86400000;
  if (today.getTime() >= minDateMs - 86400000 * 30 && today.getTime() <= maxDateMs + 86400000 * 60) {
    rulers.push({
      id: 'tl_ruler_today',
      type: 'timelineRuler',
      position: { x: todayOffset * TIMELINE_PX_PER_DAY, y: -90 },
      data: { label: 'TODAY', isToday: true },
      draggable: false, selectable: false, focusable: false,
    });
  }

  return [...rulers, ...positioned];
}

// ─── Timeline ruler node ──────────────────────────────────────────────────────
function TimelineRulerNode({ data }) {
  return (
    <Box sx={{ position: 'relative', pointerEvents: 'none', userSelect: 'none', width: 2 }}>
      <Typography sx={{
        position: 'absolute', top: 0, left: 0, transform: 'translateX(-50%)',
        fontSize: '0.58rem', fontWeight: data.isToday ? 800 : 600,
        color: data.isToday ? '#6366f1' : 'var(--tx-gh)',
        whiteSpace: 'nowrap',
      }}>
        {data.label}
      </Typography>
      <Box sx={{
        position: 'absolute', top: 20, left: 0,
        width: data.isToday ? 2 : 1,
        height: 8000,
        bgcolor: data.isToday ? '#6366f1' : 'var(--bd-lo)',
        opacity: data.isToday ? 0.7 : 0.3,
      }} />
    </Box>
  );
}

// ─── Lane background node ──────────────────────────────────────────────────────
function LaneBackgroundNode({ data }) {
  return (
    <Box sx={{
      width: data.width, height: data.height,
      bgcolor: data.isDone ? 'rgba(30,30,40,0.55)' : `${data.color}09`,
      border: `1px solid ${data.isDone ? 'rgba(100,100,120,0.3)' : `${data.color}25`}`,
      borderRadius: 3,
      pointerEvents: 'none',
      position: 'relative',
      transition: 'background-color 0.3s',
      ...(data.isDone && {
        '&::after': {
          content: '""',
          position: 'absolute', inset: 0,
          background: 'repeating-linear-gradient(135deg, transparent, transparent 12px, rgba(0,0,0,0.06) 12px, rgba(0,0,0,0.06) 13px)',
          borderRadius: 3,
          pointerEvents: 'none',
        }
      }),
    }}>
      <Typography sx={{
        position: 'absolute', top: 6, left: 10,
        fontSize: '0.6rem', fontWeight: 700,
        color: data.isDone ? 'rgba(150,150,170,0.6)' : data.color,
        textTransform: 'uppercase', letterSpacing: 1, opacity: 0.5,
        pointerEvents: 'none',
        textDecoration: data.isDone ? 'line-through' : 'none',
      }}>
        {data.label}{data.isDone ? ' · done' : ''}
      </Typography>
    </Box>
  );
}

// ─── Custom node component ────────────────────────────────────────────────────
function TrackerNode({ data, selected }) {
  const [expanded, setExpanded] = useState(false);
  const meta = NODE_META[data.type] || NODE_META.task;
  const statusMeta = STATUS_META[data.status] || STATUS_META.open;
  const showTargetDate = ['activity', 'task', 'milestone', 'pivot'].includes(data.type);
  const fmtDate = d => new Date(d + 'T00:00:00').toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' });

  const isSuggested = data.status === 'suggested';

  return (
    <Box
      sx={{
        width: '100%', minWidth: 180, borderRadius: 2,
        border: isSuggested
          ? `1.5px dashed #a78bfa`
          : `1.5px solid ${expanded ? meta.color : meta.border}`,
        bgcolor: isSuggested ? `${meta.bg}cc` : meta.bg,
        px: 1.5, pt: 1, pb: 0.75, position: 'relative', boxSizing: 'border-box',
        boxShadow: isSuggested ? `0 0 0 1px #a78bfa22 inset` : expanded ? `0 0 16px ${meta.color}33` : 'none',
        opacity: isSuggested ? 0.92 : 1,
        '&:hover': {
          border: isSuggested ? `1.5px dashed #c4b5fd` : `1.5px solid ${meta.color}`,
          boxShadow: isSuggested ? `0 0 12px #a78bfa44` : `0 0 12px ${meta.color}44`,
        },
        '&:hover .del-node-btn': { opacity: 1 },
        transition: 'all 0.15s',
      }}
    >
      <NodeResizer
        isVisible={selected}
        minWidth={180}
        minHeight={50}
        handleStyle={{ width: 8, height: 8, borderRadius: 2, background: '#6366f1', border: '2px solid #fff', zIndex: 10 }}
        lineStyle={{ borderColor: '#6366f1', borderWidth: 1, opacity: 0.5 }}
      />
      {/* Always-visible bottom-right resize handle */}
      <NodeResizeControl position="bottom-right" minWidth={180} minHeight={50}
        style={{ background: 'transparent', border: 'none', width: 14, height: 14, right: 2, bottom: 2 }}>
        <svg width="10" height="10" viewBox="0 0 10 10" style={{ display: 'block', opacity: 0.35 }}>
          <line x1="2" y1="9" x2="9" y2="2" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="5" y1="9" x2="9" y2="5" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="8" y1="9" x2="9" y2="8" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </NodeResizeControl>
      <Handle type="target" position={Position.Left} style={{ background: meta.color, border: 'none', width: 8, height: 8 }} />

      {/* Header row */}
      <Stack direction="row" alignItems="center" spacing={0.8} mb={0.3}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: isSuggested ? '#a78bfa' : meta.color, flexShrink: 0 }} />
        <Typography sx={{ fontSize: '0.63rem', color: isSuggested ? '#a78bfa' : meta.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {meta.label}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {data.type === 'workstream' && !isSuggested && (
          <Tooltip title="AI Insights" placement="top">
            <IconButton size="small"
              onClick={e => { e.stopPropagation(); data.onWorkstreamInsight && data.onWorkstreamInsight(data.id, data.label); }}
              sx={{ p: 0.25, color: '#818cf8', '&:hover': { color: '#a5b4fc', bgcolor: 'rgba(99,102,241,0.15)' } }}>
              <AutoAwesomeIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        )}
        {isSuggested
          ? <Chip label="✨ AI Suggested" size="small"
              sx={{ height: 14, fontSize: '0.52rem', fontWeight: 700, bgcolor: 'rgba(167,139,250,0.15)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.4)', '& .MuiChip-label': { px: 0.75 } }}
            />
          : <Chip label={statusMeta.label} size="small"
              sx={{ height: 14, fontSize: '0.55rem', fontWeight: 700, bgcolor: `${statusMeta.color}22`, color: statusMeta.color, border: `1px solid ${statusMeta.color}55`, '& .MuiChip-label': { px: 0.75 } }}
            />
        }
      </Stack>

      {/* Milestone sub-type badge */}
      {data.type === 'milestone' && data.sub_type && (() => {
        const st = MILESTONE_SUBTYPES.find(s => s.value === data.sub_type);
        if (!st) return null;
        return (
          <Box sx={{ mb: 0.25 }}>
            <Chip label={st.label} size="small"
              sx={{ height: 13, fontSize: '0.5rem', fontWeight: 700, bgcolor: `${st.color}22`, color: st.color, border: `1px solid ${st.color}55`, '& .MuiChip-label': { px: 0.6 } }}
            />
          </Box>
        );
      })()}

      {/* Title — click to edit */}
      <Typography
        onClick={() => data.onEdit && data.onEdit(data.id)}
        sx={{ fontSize: '0.82rem', color: 'var(--tx-hi)', fontWeight: 600, lineHeight: 1.35, wordBreak: 'break-word', cursor: 'pointer', '&:hover': { color: meta.color } }}
      >
        {data.label}
      </Typography>


      {/* AI strategy button — initiative / project / workstream / milestone:strategy */}
      {(STRATEGY_TYPES.includes(data.type) || (data.type === 'milestone' && data.sub_type === 'strategy')) && !isSuggested && (
        <Box mt={0.5}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: 'pointer', display: 'inline-flex', '&:hover': { opacity: 0.8 } }}
            onClick={e => { e.stopPropagation(); data.onSuggestStrategy && data.onSuggestStrategy(data.id, data.label, data.type, data.type === 'milestone'); }}>
            <AutoAwesomeIcon sx={{ fontSize: 11, color: '#f97316' }} />
            <Typography sx={{ fontSize: '0.62rem', color: '#f97316', fontWeight: 600 }}>
              {data.type === 'milestone' ? 'Fill with AI' : 'Suggest Strategy'}
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Mandatory date row for activity / task */}
      {showTargetDate && (
        <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: data.target_date ? '#f59e0b' : '#ef4444', flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.62rem', color: data.target_date ? '#f59e0b' : '#ef4444', fontWeight: 500 }}>
            {data.target_date ? `Due ${fmtDate(data.target_date)}` : 'No due date set'}
          </Typography>
        </Stack>
      )}

      {/* Optional date_started for workstream */}
      {data.date_started && (
        <Typography sx={{ fontSize: '0.62rem', color: 'var(--tx-ft)', mt: 0.2 }}>
          Started {fmtDate(data.date_started)}
        </Typography>
      )}

      {/* Approve / reject — suggested nodes only */}
      {isSuggested && (
        <Stack direction="row" spacing={0.75} mt={0.75}>
          <Button size="small" variant="contained"
            onClick={e => { e.stopPropagation(); data.onApprove && data.onApprove(data.id); }}
            sx={{ flex: 1, fontSize: '0.65rem', py: 0.3, minWidth: 0, textTransform: 'none', borderRadius: 1.5,
              bgcolor: 'rgba(16,185,129,0.2)', color: '#10b981', border: '1px solid rgba(16,185,129,0.4)',
              boxShadow: 'none', '&:hover': { bgcolor: 'rgba(16,185,129,0.35)', boxShadow: 'none' } }}>
            ✓ Accept
          </Button>
          <Button size="small" variant="contained"
            onClick={e => { e.stopPropagation(); data.onReject && data.onReject(data.id); }}
            sx={{ flex: 1, fontSize: '0.65rem', py: 0.3, minWidth: 0, textTransform: 'none', borderRadius: 1.5,
              bgcolor: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)',
              boxShadow: 'none', '&:hover': { bgcolor: 'rgba(239,68,68,0.3)', boxShadow: 'none' } }}>
            ✕ Reject
          </Button>
        </Stack>
      )}

      {/* Journal notes + mic buttons — activity / task / pivot (not shown for suggested) */}
      {['activity', 'task', 'milestone', 'pivot'].includes(data.type) && !isSuggested && (
        <Box mt={0.4}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
              onClick={e => { e.stopPropagation(); data.onOpenJournal && data.onOpenJournal(data.id, data.label); }}>
              <ArticleIcon sx={{ fontSize: 11, color: 'var(--tx-gh)' }} />
              <Typography sx={{ fontSize: '0.62rem', color: 'var(--tx-gh)' }}>Add notes</Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.4} sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
              onClick={e => { e.stopPropagation(); data.onRecord && data.onRecord(data.id, data.label); }}>
              <MicIcon sx={{ fontSize: 11, color: 'var(--tx-gh)' }} />
              <Typography sx={{ fontSize: '0.62rem', color: 'var(--tx-gh)' }}>Record</Typography>
            </Stack>
          </Stack>
          {/* Individual journal entries linked to this node */}
          {data.journalEntries?.map(entry => (
            <Stack key={entry.id} direction="row" alignItems="center" spacing={0.5} mt={0.3}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                onClick={e => { e.stopPropagation(); data.onOpenJournal && data.onOpenJournal(data.id, data.label, entry.id); }}>
                <ArticleIcon sx={{ fontSize: 10, color: '#6366f1', flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.6rem', color: '#a5b4fc', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {entry.title}
                </Typography>
              </Stack>
              <Box onClick={e => { e.stopPropagation(); data.onDeleteJournalEntry && data.onDeleteJournalEntry(entry.id); }}
                sx={{ cursor: 'pointer', color: 'var(--tx-dim)', '&:hover': { color: '#ef4444' }, fontSize: 10, lineHeight: 1, px: 0.3, flexShrink: 0 }}>
                ×
              </Box>
            </Stack>
          ))}
        </Box>
      )}

      {/* Pending transcription jobs for this node */}
      {data.pendingJobs?.map(job => (
        <Stack key={job.job_id} direction="row" alignItems="center" spacing={0.5} mt={0.4}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1, cursor: job.status === 'done' ? 'pointer' : 'default', '&:hover': { opacity: job.status === 'done' ? 0.8 : 1 } }}
            onClick={e => { e.stopPropagation(); job.status === 'done' && data.onReviewJob && data.onReviewJob(job); }}
          >
            {job.status === 'processing' && <HourglassEmptyIcon sx={{ fontSize: 11, color: '#f59e0b' }} className="pulse-icon" />}
            {job.status === 'done' && <CheckCircleIcon sx={{ fontSize: 11, color: '#10b981' }} />}
            {job.status === 'failed' && <ErrorIcon sx={{ fontSize: 11, color: '#ef4444' }} />}
            <Typography sx={{ fontSize: '0.62rem', color: job.status === 'done' ? '#10b981' : job.status === 'failed' ? '#ef4444' : '#f59e0b', fontWeight: 500,
              maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {job.status === 'processing' ? `Transcribing…` : job.status === 'done' ? `📋 ${job.name}` : `Failed: ${job.name}`}
            </Typography>
          </Stack>
          <Box onClick={e => { e.stopPropagation(); data.onRemoveJob && data.onRemoveJob(job.job_id); }}
            sx={{ cursor: 'pointer', color: 'var(--tx-dim)', '&:hover': { color: '#ef4444' }, fontSize: 10, lineHeight: 1, px: 0.3 }}>
            ×
          </Box>
        </Stack>
      ))}

      {/* Active duration for workstream — from first activity to today */}
      {data.type === 'workstream' && data.first_activity_date && (() => {
        const start = new Date(data.first_activity_date + 'T00:00:00');
        const today = new Date();
        const days = Math.floor((today - start) / 86400000);
        const weeks = Math.floor(days / 7);
        const months = Math.floor(days / 30.44);
        const label = months >= 2 ? `${months} months` : weeks >= 1 ? `${weeks}w ${days % 7}d` : `${days}d`;
        return (
          <Stack direction="row" alignItems="center" spacing={0.5} mt={0.4}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#a78bfa', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.62rem', color: '#a78bfa', fontWeight: 600 }}>
              Active {label}
            </Typography>
          </Stack>
        );
      })()}

      {/* Expand / collapse toggle */}
      <Box
        onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 0.5, cursor: 'pointer', color: 'var(--tx-gh)', '&:hover': { color: meta.color }, transition: 'color 0.15s' }}
      >
        {expanded
          ? <ExpandMoreIcon sx={{ fontSize: 14 }} />
          : <ChevronRightIcon sx={{ fontSize: 14 }} />
        }
      </Box>

      {/* Expanded detail */}
      <Collapse in={expanded} timeout={160}>
        <Box sx={{ mt: 0.5, pt: 0.75, borderTop: `1px solid ${meta.border}` }}>
          {GOAL_TYPES.includes(data.type) && data.goal && (
            <Box sx={{ mb: 0.75, borderLeft: `2px solid ${meta.color}55`, pl: 0.75 }}>
              <Typography sx={{ fontSize: '0.6rem', color: meta.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.3 }}>Goal</Typography>
              {data.goal.split('\n').filter(l => l.trim()).map((line, i) => (
                <Typography key={i} sx={{ fontSize: '0.7rem', color: 'var(--tx-lo)', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {line}
                </Typography>
              ))}
            </Box>
          )}
          <Box>
            <Typography sx={{ fontSize: '0.6rem', color: 'var(--tx-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.3 }}>Description</Typography>
            {data.description ? (
              <Typography sx={{ fontSize: '0.72rem', color: 'var(--tx-lo)', lineHeight: 1.55, whiteSpace: 'pre-wrap', mb: 0.75 }}>
                {data.description}
              </Typography>
            ) : (
              <Typography sx={{ fontSize: '0.68rem', color: 'var(--tx-gh)', fontStyle: 'italic', mb: 0.5 }}>No description</Typography>
            )}
          </Box>
          {data.custom_fields?.length > 0 && (
            <Box>
              {data.custom_fields.map((cf, i) => (
                <Stack key={i} direction="row" spacing={0.5} mb={0.3}>
                  <Typography sx={{ fontSize: '0.62rem', color: 'var(--tx-ft)', fontWeight: 600, flexShrink: 0 }}>{cf.key}:</Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: 'var(--tx-lo)' }}>{cf.value}</Typography>
                </Stack>
              ))}
            </Box>
          )}
        </Box>
      </Collapse>

      {/* Add child button (top-right) */}
      {ALLOWED_CHILDREN[data.type]?.length > 0 && (
        <IconButton size="small"
          onClick={e => { e.stopPropagation(); data.onAddChild && data.onAddChild(data.id, data.type); }}
          sx={{ position: 'absolute', top: -10, right: -10, width: 20, height: 20, bgcolor: 'var(--bg-card)', border: '1px solid var(--bd-md)', color: '#6366f1', '&:hover': { bgcolor: '#6366f1', color: '#fff' }, p: 0 }}
        >
          <AddIcon sx={{ fontSize: 12 }} />
        </IconButton>
      )}
      {/* Delete button (bottom-right, on hover) */}
      <IconButton className="del-node-btn" size="small"
        onClick={e => { e.stopPropagation(); data.onDelete && data.onDelete(data.id); }}
        sx={{ position: 'absolute', bottom: -10, right: -10, width: 20, height: 20, bgcolor: 'var(--bg-card)', border: '1px solid var(--bd-md)', color: '#ef4444', opacity: 0, transition: 'opacity 0.15s', '&:hover': { bgcolor: '#ef4444', color: '#fff', opacity: 1 }, p: 0 }}
      >
        <DeleteOutlineIcon sx={{ fontSize: 12 }} />
      </IconButton>

      <Handle type="source" position={Position.Right} style={{ background: meta.color, border: 'none', width: 8, height: 8 }} />
    </Box>
  );
}

const nodeTypes = { trackerNode: TrackerNode, laneBackground: LaneBackgroundNode, timelineRuler: TimelineRulerNode };

// ─── Node Form Drawer ─────────────────────────────────────────────────────────
function NodeFormDrawer({ open, onClose, node, parentId, parentType, defaultType, allNodes, edges, onSave, onDeleteNode, onAddEdge, onRemoveEdge }) {
  const [form, setForm] = useState({ type: 'activity', sub_type: '', title: '', status: 'open', description: '', goal: '', target_date: '', date_started: '', custom_fields: [] });
  const [edgeType, setEdgeType] = useState('parent_of');
  const [connectTo, setConnectTo] = useState(parentId || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newParentId, setNewParentId] = useState(null);
  const [newParentEdgeType, setNewParentEdgeType] = useState('parent_of');
  const [addingParent, setAddingParent] = useState(false);
  const [linkMode, setLinkMode] = useState(false); // "link existing" vs "create new"
  const [linkTargetId, setLinkTargetId] = useState(null);
  const [linkEdgeType, setLinkEdgeType] = useState('parent_of');
  const [linkSaving, setLinkSaving] = useState(false);

  useEffect(() => {
    if (node) {
      setForm({ type: node.type === 'pivot' ? 'milestone' : node.type, sub_type: node.sub_type || (node.type === 'pivot' ? 'pivot' : ''), title: node.title, status: node.status, description: node.description || '', goal: node.goal || '', target_date: node.target_date || '', date_started: node.date_started || '', custom_fields: node.custom_fields || [] });
      setConnectTo(null);
      setEdgeType('parent_of');
    } else {
      const childType = defaultType || (parentType ? (ALLOWED_CHILDREN[parentType]?.[0] || 'task') : 'initiative');
      setForm({ type: childType, sub_type: '', title: '', status: 'open', description: '', goal: '', target_date: '', date_started: '', custom_fields: [] });
      setConnectTo(parentId || null);
      setEdgeType(parentType === 'activity' ? 'leads_to' : 'parent_of');
      setLinkMode(false); setLinkTargetId(null); setLinkEdgeType('parent_of');
    }
  }, [node, parentId, parentType, defaultType, open]);

  const allowedTypes = parentType
    ? ALLOWED_CHILDREN[parentType] || []
    : ROOT_TYPES;

  function addField() { setForm(f => ({ ...f, custom_fields: [...f.custom_fields, { key: '', value: '' }] })); }
  function updateField(i, k, v) { setForm(f => { const cf = [...f.custom_fields]; cf[i] = { ...cf[i], [k]: v }; return { ...f, custom_fields: cf }; }); }
  function removeField(i) { setForm(f => ({ ...f, custom_fields: f.custom_fields.filter((_, j) => j !== i) })); }

  async function handleSave() {
    if (!form.title.trim()) { setError('Title is required'); return; }
    if (['activity', 'task', 'milestone', 'pivot'].includes(form.type) && !form.target_date) { setError('Target date is required for activities, tasks and milestones'); return; }
    setSaving(true); setError('');
    try {
      await onSave(form, node?.id, connectTo, edgeType);
      onClose();
    } catch (e) { setError(e.message || 'Save failed'); }
    finally { setSaving(false); }
  }

  const connectableNodes = allNodes.filter(n => n.id !== node?.id);
  const meta = NODE_META[form.type] || NODE_META.task;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} variant="temporary"
      sx={{ '& .MuiDrawer-paper': { width: { xs: '100%', sm: 440 }, bgcolor: 'var(--bg-surf)', borderLeft: '1px solid var(--bd-lo)', display: 'flex', flexDirection: 'column' } }}
    >
      <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid var(--bd-lo)', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: meta.color }} />
        <Typography sx={{ fontWeight: 700, color: 'var(--tx-hi)', flex: 1 }}>
          {node ? `Edit ${NODE_META[form.type]?.label}` : linkMode ? 'Link Existing Node' : `New ${NODE_META[form.type]?.label || 'Node'}`}
        </Typography>
        {/* Create / Link toggle (only when adding a child) */}
        {!node && parentId && (
          <Stack direction="row" sx={{ bgcolor: 'var(--bg-card)', borderRadius: 1.5, p: 0.25, gap: 0.25 }}>
            <Button size="small" onClick={() => setLinkMode(false)}
              sx={{ minWidth: 0, px: 1, py: 0.25, borderRadius: 1.25, textTransform: 'none', fontSize: '0.7rem', fontWeight: 600,
                bgcolor: !linkMode ? '#6366f1' : 'transparent', color: !linkMode ? '#fff' : 'var(--tx-dim)', '&:hover': { bgcolor: !linkMode ? '#4f46e5' : 'var(--bg-hover)' } }}>
              Create
            </Button>
            <Button size="small" onClick={() => setLinkMode(true)}
              sx={{ minWidth: 0, px: 1, py: 0.25, borderRadius: 1.25, textTransform: 'none', fontSize: '0.7rem', fontWeight: 600,
                bgcolor: linkMode ? '#6366f1' : 'transparent', color: linkMode ? '#fff' : 'var(--tx-dim)', '&:hover': { bgcolor: linkMode ? '#4f46e5' : 'var(--bg-hover)' } }}>
              Link
            </Button>
          </Stack>
        )}
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--tx-dim)' }}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {error && <Alert severity="error" sx={{ fontSize: '0.8rem' }}>{error}</Alert>}

        {/* ── Link-existing mode ─────────────────────────────────────────── */}
        {!node && linkMode && (
          <>
            <Typography sx={{ fontSize: '0.78rem', color: 'var(--tx-lo)' }}>
              Select an existing node to connect as a child of the current node.
            </Typography>
            <Autocomplete
              size="small"
              options={allNodes.filter(n => n.id !== parentId)}
              getOptionLabel={n => `${NODE_META[n.type]?.label}: ${n.title}`}
              value={allNodes.find(n => n.id === linkTargetId) || null}
              onChange={(_, v) => setLinkTargetId(v?.id || null)}
              renderInput={params => <TextField {...params} placeholder="Search existing nodes…"
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'var(--bg-card)', color: 'var(--tx-md)', '& fieldset': { borderColor: 'var(--tx-gh)' } } }}
              />}
              renderOption={(props, n) => (
                <Box component="li" {...props}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: NODE_META[n.type]?.color }} />
                    <Typography sx={{ fontSize: '0.8rem' }}>{NODE_META[n.type]?.label}: {n.title}</Typography>
                  </Stack>
                </Box>
              )}
              sx={{ '& .MuiAutocomplete-popupIndicator': { color: 'var(--tx-dim)' }, '& .MuiAutocomplete-clearIndicator': { color: 'var(--tx-dim)' } }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ color: 'var(--tx-dim)' }}>Edge type</InputLabel>
              <Select value={linkEdgeType} label="Edge type" onChange={e => setLinkEdgeType(e.target.value)}
                sx={{ color: 'var(--tx-md)', bgcolor: 'var(--bg-card)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--tx-gh)' } }}
              >
                {Object.entries(EDGE_META).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
              </Select>
            </FormControl>
          </>
        )}

        {/* ── Create-new mode ────────────────────────────────────────────── */}
        {(node || !linkMode) && <>

        {/* Type */}
        <FormControl size="small" fullWidth>
          <InputLabel sx={{ color: 'var(--tx-dim)' }}>Type</InputLabel>
          <Select value={form.type} label="Type" onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
            sx={{ color: 'var(--tx-md)', bgcolor: 'var(--bg-card)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--tx-gh)' } }}
          >
            {(node ? NODE_TYPES_ORDER : allowedTypes).map(t => (
              <MenuItem key={t} value={t}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: NODE_META[t].color }} />
                  <span>{NODE_META[t].label}</span>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Milestone sub-type */}
        {form.type === 'milestone' && (
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ color: 'var(--tx-dim)' }}>Milestone Type</InputLabel>
            <Select value={form.sub_type} label="Milestone Type" onChange={e => setForm(f => ({ ...f, sub_type: e.target.value }))}
              sx={{ color: 'var(--tx-md)', bgcolor: 'var(--bg-card)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--tx-gh)' } }}
            >
              <MenuItem value=""><em style={{ color: 'var(--tx-ft)' }}>None</em></MenuItem>
              {MILESTONE_SUBTYPES.map(st => (
                <MenuItem key={st.value} value={st.value}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: st.color }} />
                    <span>{st.label}</span>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Title */}
        <TextField size="small" fullWidth label="Title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'var(--bg-card)', color: 'var(--tx-md)', '& fieldset': { borderColor: 'var(--tx-gh)' } }, '& .MuiInputLabel-root': { color: 'var(--tx-dim)' } }}
        />

        {/* Status */}
        <FormControl size="small" fullWidth>
          <InputLabel sx={{ color: 'var(--tx-dim)' }}>Status</InputLabel>
          <Select value={form.status} label="Status" onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
            sx={{ color: 'var(--tx-md)', bgcolor: 'var(--bg-card)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--tx-gh)' } }}
          >
            {Object.entries(STATUS_META).map(([k, v]) => (
              <MenuItem key={k} value={k}><Stack direction="row" spacing={1} alignItems="center"><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: v.color }} /><span>{v.label}</span></Stack></MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Description */}
        <TextField size="small" fullWidth label="Description" multiline rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'var(--bg-card)', color: 'var(--tx-md)', '& fieldset': { borderColor: 'var(--tx-gh)' } }, '& .MuiInputLabel-root': { color: 'var(--tx-dim)' } }}
        />

        {/* Goal — initiative / project / opportunity / workstream */}
        {GOAL_TYPES.includes(form.type) && (
          <TextField size="small" fullWidth label="Goal" placeholder={'What does success look like?\n\n- Be specific\n- Use point form\n- Include measurable outcomes'}
            value={form.goal} onChange={e => setForm(f => ({ ...f, goal: e.target.value }))}
            multiline minRows={3} maxRows={10}
            helperText="The intended outcome — supports point form"
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'var(--bg-card)', color: 'var(--tx-md)', '& fieldset': { borderColor: 'var(--tx-gh)' }, alignItems: 'flex-start' }, '& .MuiInputLabel-root': { color: 'var(--tx-dim)' }, '& .MuiFormHelperText-root': { color: 'var(--tx-ft)' } }}
          />
        )}

        {/* Date fields */}
        {['activity', 'task', 'milestone', 'pivot'].includes(form.type) && (
          <TextField
            size="small" fullWidth type="date" required
            label="Target Date *"
            value={form.target_date}
            onChange={e => setForm(f => ({ ...f, target_date: e.target.value }))}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'var(--bg-card)', color: 'var(--tx-md)', '& fieldset': { borderColor: form.target_date ? 'var(--tx-gh)' : '#ef4444' }, colorScheme: 'dark' }, '& .MuiInputLabel-root': { color: 'var(--tx-dim)' } }}
          />
        )}
        {form.type === 'workstream' && (
          <TextField
            size="small" fullWidth type="date"
            label="Date Started"
            value={form.date_started}
            onChange={e => setForm(f => ({ ...f, date_started: e.target.value }))}
            slotProps={{ inputLabel: { shrink: true } }}
            helperText="Optional"
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'var(--bg-card)', color: 'var(--tx-md)', '& fieldset': { borderColor: 'var(--tx-gh)' }, colorScheme: 'dark' }, '& .MuiInputLabel-root': { color: 'var(--tx-dim)' }, '& .MuiFormHelperText-root': { color: 'var(--tx-ft)' } }}
          />
        )}

        {/* Parents (edit mode only) */}
        {node && (
          <Box>
            <Stack direction="row" alignItems="center" mb={1}>
              <Typography sx={{ fontSize: '0.75rem', color: 'var(--tx-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 }}>Parents</Typography>
              <Tooltip title="Add parent">
                <IconButton size="small" onClick={() => setAddingParent(v => !v)} sx={{ color: '#6366f1', bgcolor: 'var(--bg-card)', width: 24, height: 24 }}>
                  <AddIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Stack>

            {/* Existing parents */}
            {(edges || []).filter(e => e.target === node.id).map(e => {
              const parentNode = allNodes.find(n => n.id === e.source);
              if (!parentNode) return null;
              const meta = NODE_META[parentNode.type] || NODE_META.task;
              return (
                <Stack key={e.id} direction="row" alignItems="center" spacing={1} mb={0.75}
                  sx={{ bgcolor: 'var(--bg-card)', border: '1px solid var(--bd-lo)', borderRadius: 1.5, px: 1.25, py: 0.6 }}
                >
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: meta.color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.75rem', color: 'var(--tx-md)', flex: 1 }}>
                    <span style={{ color: meta.color, fontWeight: 600 }}>{meta.label}</span>: {parentNode.title}
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: 'var(--tx-ft)' }}>{EDGE_META[e.type]?.label || e.type}</Typography>
                  <IconButton size="small" onClick={() => onRemoveEdge && onRemoveEdge(e.id)}
                    sx={{ color: 'var(--tx-ft)', '&:hover': { color: '#ef4444' }, p: 0.3 }}>
                    <CloseIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Stack>
              );
            })}
            {(edges || []).filter(e => e.target === node.id).length === 0 && !addingParent && (
              <Typography sx={{ fontSize: '0.72rem', color: 'var(--tx-gh)', fontStyle: 'italic' }}>No parents — click + to add</Typography>
            )}

            {/* Add parent row */}
            {addingParent && (
              <Box sx={{ mt: 0.5 }}>
                <Autocomplete
                  size="small"
                  options={allNodes.filter(n => n.id !== node.id)}
                  getOptionLabel={n => `${NODE_META[n.type]?.label}: ${n.title}`}
                  value={allNodes.find(n => n.id === newParentId) || null}
                  onChange={(_, v) => setNewParentId(v?.id || null)}
                  renderInput={params => <TextField {...params} placeholder="Select parent node"
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'var(--bg-card)', color: 'var(--tx-md)', '& fieldset': { borderColor: 'var(--tx-gh)' } } }}
                  />}
                  renderOption={(props, n) => (
                    <Box component="li" {...props}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: NODE_META[n.type]?.color }} />
                        <Typography sx={{ fontSize: '0.8rem' }}>{NODE_META[n.type]?.label}: {n.title}</Typography>
                      </Stack>
                    </Box>
                  )}
                  sx={{ mb: 1, '& .MuiAutocomplete-popupIndicator': { color: 'var(--tx-dim)' }, '& .MuiAutocomplete-clearIndicator': { color: 'var(--tx-dim)' } }}
                />
                <Stack direction="row" spacing={1}>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel sx={{ color: 'var(--tx-dim)' }}>Edge type</InputLabel>
                    <Select value={newParentEdgeType} label="Edge type" onChange={e => setNewParentEdgeType(e.target.value)}
                      sx={{ color: 'var(--tx-md)', bgcolor: 'var(--bg-card)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--tx-gh)' } }}
                    >
                      {Object.entries(EDGE_META).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Button size="small" variant="contained" disabled={!newParentId}
                    onClick={async () => {
                      if (!newParentId) return;
                      await onAddEdge(newParentId, node.id, newParentEdgeType);
                      setNewParentId(null);
                      setNewParentEdgeType('parent_of');
                      setAddingParent(false);
                    }}
                    sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, textTransform: 'none', flexShrink: 0 }}
                  >
                    Add
                  </Button>
                </Stack>
              </Box>
            )}
          </Box>
        )}

        {/* Connection */}
        {!node && (
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: 'var(--tx-dim)', mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Connect to</Typography>
            <Autocomplete
              size="small"
              options={connectableNodes}
              getOptionLabel={n => `${NODE_META[n.type]?.label}: ${n.title}`}
              value={connectableNodes.find(n => n.id === connectTo) || null}
              onChange={(_, v) => setConnectTo(v?.id || null)}
              renderInput={params => <TextField {...params} placeholder="Select parent / predecessor node"
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'var(--bg-card)', color: 'var(--tx-md)', '& fieldset': { borderColor: 'var(--tx-gh)' } } }}
              />}
              renderOption={(props, n) => (
                <Box component="li" {...props}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: NODE_META[n.type]?.color }} />
                    <Typography sx={{ fontSize: '0.8rem' }}>{NODE_META[n.type]?.label}: {n.title}</Typography>
                  </Stack>
                </Box>
              )}
              sx={{ '& .MuiAutocomplete-popupIndicator': { color: 'var(--tx-dim)' }, '& .MuiAutocomplete-clearIndicator': { color: 'var(--tx-dim)' } }}
            />
            {connectTo && (
              <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                <InputLabel sx={{ color: 'var(--tx-dim)' }}>Edge type</InputLabel>
                <Select value={edgeType} label="Edge type" onChange={e => setEdgeType(e.target.value)}
                  sx={{ color: 'var(--tx-md)', bgcolor: 'var(--bg-card)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--tx-gh)' } }}
                >
                  {Object.entries(EDGE_META).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                </Select>
              </FormControl>
            )}
          </Box>
        )}

        {/* Custom fields */}
        <Box>
          <Stack direction="row" alignItems="center" mb={1}>
            <Typography sx={{ fontSize: '0.75rem', color: 'var(--tx-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 }}>Custom Fields</Typography>
            <Tooltip title="Add field">
              <IconButton size="small" onClick={addField} sx={{ color: '#6366f1', bgcolor: 'var(--bg-card)', width: 24, height: 24 }}><AddIcon sx={{ fontSize: 14 }} /></IconButton>
            </Tooltip>
          </Stack>
          {form.custom_fields.map((cf, i) => (
            <Stack key={i} direction="row" spacing={1} mb={1} alignItems="center">
              <TextField size="small" placeholder="Field name" value={cf.key} onChange={e => updateField(i, 'key', e.target.value)}
                sx={{ flex: 1, '& .MuiOutlinedInput-root': { bgcolor: 'var(--bg-card)', color: 'var(--tx-md)', '& fieldset': { borderColor: 'var(--tx-gh)' } } }}
              />
              <TextField size="small" placeholder="Value" value={cf.value} onChange={e => updateField(i, 'value', e.target.value)}
                sx={{ flex: 2, '& .MuiOutlinedInput-root': { bgcolor: 'var(--bg-card)', color: 'var(--tx-md)', '& fieldset': { borderColor: 'var(--tx-gh)' } } }}
              />
              <IconButton size="small" onClick={() => removeField(i)} sx={{ color: '#ef4444' }}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton>
            </Stack>
          ))}
          {form.custom_fields.length === 0 && (
            <Typography sx={{ fontSize: '0.75rem', color: 'var(--tx-gh)', fontStyle: 'italic' }}>No custom fields — click + to add</Typography>
          )}
        </Box>
        </>}
      </Box>

      <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid var(--bd-lo)', display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        {node && onDeleteNode && (
          <Button
            onClick={() => { onDeleteNode(node.id); onClose(); }}
            sx={{ color: '#ef4444', textTransform: 'none', mr: 'auto', '&:hover': { bgcolor: 'rgba(239,68,68,0.08)' } }}
            startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
          >
            Delete
          </Button>
        )}
        <Button onClick={onClose} sx={{ color: 'var(--tx-dim)', textTransform: 'none' }}>Cancel</Button>
        {linkMode ? (
          <Button variant="contained" disabled={!linkTargetId || linkSaving}
            onClick={async () => {
              if (!linkTargetId || !parentId) return;
              setLinkSaving(true);
              try {
                await onAddEdge(parentId, linkTargetId, linkEdgeType);
                onClose();
              } finally {
                setLinkSaving(false);
              }
            }}
            sx={{ bgcolor: '#6366f1', '&:hover': { filter: 'brightness(1.1)' }, textTransform: 'none', borderRadius: 2 }}
          >
            {linkSaving ? 'Linking…' : 'Link'}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: meta.color, '&:hover': { filter: 'brightness(1.1)' }, textTransform: 'none', borderRadius: 2 }}
          >
            {saving ? 'Saving…' : node ? 'Update' : 'Create'}
          </Button>
        )}
      </Box>
    </Drawer>
  );
}

// ─── List view panel ─────────────────────────────────────────────────────────
function ListViewPanel({ allNodes, edges, focusId, journalEntriesMap, navigate }) {
  const [expanded, setExpanded] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  function getDescendantsLocal(rootId) {
    const visited = new Set([rootId]);
    const queue = [rootId];
    while (queue.length) {
      const cur = queue.shift();
      edges.filter(e => e.source === cur).forEach(e => {
        if (!visited.has(e.target)) { visited.add(e.target); queue.push(e.target); }
      });
    }
    return visited;
  }

  const scopeIds = focusId ? getDescendantsLocal(focusId) : new Set(allNodes.map(n => n.id));
  const scopeNodes = allNodes.filter(n => scopeIds.has(n.id));

  const fmtDate = d => new Date(d + 'T00:00:00').toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' });
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const sortByDate = arr => [...arr].sort((a, b) => {
    if (!a.target_date && !b.target_date) return 0;
    if (!a.target_date) return 1;
    if (!b.target_date) return -1;
    return a.target_date.localeCompare(b.target_date);
  });

  const toggleGroup = key => setCollapsedGroups(prev => {
    const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s;
  });

  // ── Build hierarchy: top-levels → workstreams → nodes ──────────────────────
  const TOP_LEVEL_TYPES = ['initiative', 'project', 'opportunity'];
  const topLevelNodes = scopeNodes.filter(n => TOP_LEVEL_TYPES.includes(n.type));

  const hierarchy = topLevelNodes.map(root => {
    const rootDescIds = getDescendantsLocal(root.id);
    const rootDescNodes = scopeNodes.filter(n => rootDescIds.has(n.id) && n.id !== root.id);

    const wsNodes = rootDescNodes.filter(n => n.type === 'workstream');
    const wsAssigned = new Set();

    const workstreamGroups = wsNodes.map(ws => {
      const wsDescIds = getDescendantsLocal(ws.id);
      const children = rootDescNodes.filter(n => wsDescIds.has(n.id) && n.id !== ws.id && n.type !== 'workstream');
      children.forEach(n => wsAssigned.add(n.id));
      wsAssigned.add(ws.id);
      return { id: ws.id, label: ws.title, nodes: sortByDate(children) };
    });

    // Direct children of root not captured by any workstream
    const rootGeneral = sortByDate(rootDescNodes.filter(n => !wsAssigned.has(n.id) && n.type !== 'workstream'));

    return { root, workstreams: workstreamGroups, general: rootGeneral };
  });

  // Nodes that aren't under any top-level (pure orphans / standalone workstreams)
  const assignedToRoot = new Set(topLevelNodes.flatMap(r => [...getDescendantsLocal(r.id)]));
  const orphanWorkstreams = scopeNodes.filter(n => n.type === 'workstream' && !assignedToRoot.has(n.id));
  const orphanWsAssigned = new Set();
  const orphanWsGroups = orphanWorkstreams.map(ws => {
    const wsDescIds = getDescendantsLocal(ws.id);
    const children = scopeNodes.filter(n => wsDescIds.has(n.id) && n.id !== ws.id && n.type !== 'workstream');
    children.forEach(n => orphanWsAssigned.add(n.id));
    orphanWsAssigned.add(ws.id);
    return { id: ws.id, label: ws.title, nodes: sortByDate(children) };
  });
  const bareOrphans = sortByDate(scopeNodes.filter(n =>
    !assignedToRoot.has(n.id) && !orphanWsAssigned.has(n.id) && !TOP_LEVEL_TYPES.includes(n.type) && n.type !== 'workstream'
  ));

  function NodeRow({ node }) {
    const meta = NODE_META[node.type] || NODE_META.task;
    const statusMeta = STATUS_META[node.status] || STATUS_META.open;
    const isPast = node.target_date && new Date(node.target_date + 'T00:00:00') < today && node.status !== 'done';
    const isOpen = expanded === node.id;
    const journalEntries = journalEntriesMap[node.id] || [];
    const subType = node.type === 'milestone' && node.sub_type
      ? MILESTONE_SUBTYPES.find(s => s.value === node.sub_type) : null;

    return (
      <Box sx={{ mb: 0.5, borderRadius: 1.5, border: `1px solid ${isOpen ? meta.border : 'var(--bd-lo)'}`, overflow: 'hidden', transition: 'border-color 0.15s' }}>
        <Box onClick={() => setExpanded(isOpen ? null : node.id)}
          sx={{ px: 1.5, py: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1,
            bgcolor: isOpen ? `${meta.bg}ee` : 'var(--bg-surf)',
            '&:hover': { bgcolor: `${meta.bg}99` }, transition: 'background-color 0.15s' }}
        >
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: subType ? subType.color : meta.color, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              <Typography sx={{ fontSize: '0.82rem', color: 'var(--tx-hi)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: { xs: 160, sm: 300 } }}>
                {node.title}
              </Typography>
              <Chip label={subType ? subType.label : meta.label} size="small"
                sx={{ height: 14, fontSize: '0.52rem', fontWeight: 700,
                  bgcolor: `${subType ? subType.color : meta.color}22`,
                  color: subType ? subType.color : meta.color,
                  border: `1px solid ${subType ? subType.color : meta.color}44`,
                  '& .MuiChip-label': { px: 0.6 } }} />
            </Stack>
          </Box>
          <Box sx={{ flexShrink: 0, textAlign: 'right', minWidth: 72 }}>
            {node.target_date
              ? <Typography sx={{ fontSize: '0.68rem', color: isPast ? '#ef4444' : '#f59e0b', fontWeight: 600 }}>{fmtDate(node.target_date)}</Typography>
              : <Typography sx={{ fontSize: '0.65rem', color: 'var(--tx-gh)' }}>No date</Typography>}
          </Box>
          <ExpandMoreIcon sx={{ fontSize: 16, color: 'var(--tx-dim)', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
        </Box>
        <Collapse in={isOpen} timeout={160}>
          <Box sx={{ px: 2, py: 1.25, bgcolor: meta.bg, borderTop: `1px solid ${meta.border}55` }}>
            <Stack direction="row" spacing={0.75} mb={1} flexWrap="wrap">
              <Chip label={statusMeta.label} size="small"
                sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, bgcolor: `${statusMeta.color}22`, color: statusMeta.color, border: `1px solid ${statusMeta.color}55`, '& .MuiChip-label': { px: 0.75 } }} />
              <Chip label={meta.label} size="small"
                sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, bgcolor: `${meta.color}18`, color: meta.color, border: `1px solid ${meta.color}40`, '& .MuiChip-label': { px: 0.75 } }} />
            </Stack>
            {node.description
              ? <Typography sx={{ fontSize: '0.75rem', color: 'var(--tx-lo)', lineHeight: 1.55, mb: 1, whiteSpace: 'pre-wrap' }}>{node.description}</Typography>
              : <Typography sx={{ fontSize: '0.72rem', color: 'var(--tx-gh)', fontStyle: 'italic', mb: 1 }}>No description</Typography>}
            {node.custom_fields?.length > 0 && (
              <Box mb={1}>
                {node.custom_fields.map((cf, i) => (
                  <Stack key={i} direction="row" spacing={0.5} mb={0.3}>
                    <Typography sx={{ fontSize: '0.65rem', color: 'var(--tx-ft)', fontWeight: 600 }}>{cf.key}:</Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: 'var(--tx-lo)' }}>{cf.value}</Typography>
                  </Stack>
                ))}
              </Box>
            )}
            {journalEntries.length > 0 && (
              <Box mb={0.75}>
                <Typography sx={{ fontSize: '0.6rem', color: 'var(--tx-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5 }}>Notes</Typography>
                {journalEntries.map(entry => (
                  <Stack key={entry.id} direction="row" alignItems="center" spacing={0.5} mb={0.3}
                    onClick={() => navigate(`/journal?nodeId=${encodeURIComponent(node.id)}&nodeTitle=${encodeURIComponent(node.title)}&entryId=${encodeURIComponent(entry.id)}`)}
                    sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}>
                    <ArticleIcon sx={{ fontSize: 10, color: '#6366f1', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.65rem', color: '#a5b4fc' }}>{entry.title}</Typography>
                  </Stack>
                ))}
              </Box>
            )}
            <Button size="small"
              onClick={() => navigate(`/journal?nodeId=${encodeURIComponent(node.id)}&nodeTitle=${encodeURIComponent(node.title)}`)}
              sx={{ fontSize: '0.65rem', color: '#6366f1', textTransform: 'none', p: 0, minWidth: 0, '&:hover': { bgcolor: 'transparent', opacity: 0.75 } }}>
              + Add notes
            </Button>
          </Box>
        </Collapse>
      </Box>
    );
  }

  function WorkstreamSection({ ws, indent = false }) {
    const wsMeta = NODE_META.workstream;
    return (
      <Box key={ws.id} sx={{ mb: 1 }}>
        <Box onClick={() => toggleGroup(ws.id)}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, cursor: 'pointer',
            px: 1, py: 0.4, borderRadius: 1, '&:hover': { bgcolor: 'var(--bg-hover)' } }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: wsMeta.color, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: wsMeta.color, textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 }}>
            {ws.label}
          </Typography>
          <Typography sx={{ fontSize: '0.6rem', color: 'var(--tx-gh)' }}>{ws.nodes.length}</Typography>
          <ExpandMoreIcon sx={{ fontSize: 13, color: 'var(--tx-dim)', transform: collapsedGroups.has(ws.id) ? 'rotate(-90deg)' : 'none', transition: 'transform 0.2s' }} />
        </Box>
        <Collapse in={!collapsedGroups.has(ws.id)} timeout={150}>
          <Box sx={{ pl: 1.5, borderLeft: `2px solid ${wsMeta.color}33` }}>
            {ws.nodes.length === 0
              ? <Typography sx={{ fontSize: '0.68rem', color: 'var(--tx-gh)', fontStyle: 'italic', py: 0.4 }}>No items</Typography>
              : ws.nodes.map(node => <NodeRow key={node.id} node={node} />)}
          </Box>
        </Collapse>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', p: 2, bgcolor: 'var(--bg-base)' }}>
      <Typography sx={{ fontSize: '0.68rem', color: 'var(--tx-dim)', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700, mb: 1.5 }}>
        List · {scopeNodes.length} nodes
      </Typography>

      {/* Hierarchy: top-level → workstreams → nodes */}
      {hierarchy.map(({ root, workstreams: wsList, general: rootGeneral }) => {
        const rootMeta = NODE_META[root.type] || NODE_META.task;
        const rootKey = root.id;
        return (
          <Box key={rootKey} sx={{ mb: 2.5 }}>
            {/* Initiative / Project / Opportunity header */}
            <Box onClick={() => toggleGroup(rootKey)}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, cursor: 'pointer',
                px: 1, py: 0.6, borderRadius: 1.5, bgcolor: `${rootMeta.bg}`, border: `1px solid ${rootMeta.border}`,
                '&:hover': { border: `1px solid ${rootMeta.color}88` } }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: rootMeta.color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: rootMeta.color, letterSpacing: 0.3, flex: 1 }}>
                {root.title}
              </Typography>
              <Chip label={rootMeta.label} size="small"
                sx={{ height: 14, fontSize: '0.5rem', fontWeight: 700, bgcolor: `${rootMeta.color}22`, color: rootMeta.color, border: `1px solid ${rootMeta.color}44`, '& .MuiChip-label': { px: 0.6 } }} />
              <ExpandMoreIcon sx={{ fontSize: 15, color: 'var(--tx-dim)', transform: collapsedGroups.has(rootKey) ? 'rotate(-90deg)' : 'none', transition: 'transform 0.2s' }} />
            </Box>
            <Collapse in={!collapsedGroups.has(rootKey)} timeout={160}>
              <Box sx={{ pl: 1.5, borderLeft: `2px solid ${rootMeta.color}44` }}>
                {/* Workstreams under this root */}
                {wsList.map(ws => <WorkstreamSection key={ws.id} ws={ws} />)}
                {/* Direct children not under any workstream */}
                {rootGeneral.length > 0 && (
                  <Box sx={{ mt: wsList.length > 0 ? 0.5 : 0 }}>
                    {wsList.length > 0 && (
                      <Box onClick={() => toggleGroup(`${rootKey}__general`)}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, cursor: 'pointer',
                          px: 1, py: 0.4, borderRadius: 1, '&:hover': { bgcolor: 'var(--bg-hover)' } }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'var(--tx-gh)', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--tx-dim)', textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 }}>
                          Other
                        </Typography>
                        <Typography sx={{ fontSize: '0.6rem', color: 'var(--tx-gh)' }}>{rootGeneral.length}</Typography>
                        <ExpandMoreIcon sx={{ fontSize: 13, color: 'var(--tx-dim)', transform: collapsedGroups.has(`${rootKey}__general`) ? 'rotate(-90deg)' : 'none', transition: 'transform 0.2s' }} />
                      </Box>
                    )}
                    <Collapse in={!collapsedGroups.has(`${rootKey}__general`)} timeout={150}>
                      <Box sx={wsList.length > 0 ? { pl: 1.5, borderLeft: '2px solid var(--bd-lo)' } : {}}>
                        {rootGeneral.map(node => <NodeRow key={node.id} node={node} />)}
                      </Box>
                    </Collapse>
                  </Box>
                )}
                {wsList.length === 0 && rootGeneral.length === 0 && (
                  <Typography sx={{ fontSize: '0.7rem', color: 'var(--tx-gh)', fontStyle: 'italic', py: 0.5 }}>No items</Typography>
                )}
              </Box>
            </Collapse>
          </Box>
        );
      })}

      {/* Orphan workstreams (not under any initiative/project/opportunity) */}
      {orphanWsGroups.map(ws => <WorkstreamSection key={ws.id} ws={ws} />)}

      {/* Bare orphan nodes */}
      {bareOrphans.length > 0 && (
        <Box>
          {(hierarchy.length > 0 || orphanWsGroups.length > 0) && (
            <Box onClick={() => toggleGroup('__orphans__')}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, cursor: 'pointer',
                px: 1, py: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'var(--bg-hover)' } }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'var(--tx-gh)', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--tx-dim)', textTransform: 'uppercase', letterSpacing: 0.6, flex: 1 }}>General</Typography>
              <Typography sx={{ fontSize: '0.62rem', color: 'var(--tx-gh)' }}>{bareOrphans.length}</Typography>
              <ExpandMoreIcon sx={{ fontSize: 14, color: 'var(--tx-dim)', transform: collapsedGroups.has('__orphans__') ? 'rotate(-90deg)' : 'none', transition: 'transform 0.2s' }} />
            </Box>
          )}
          <Collapse in={!collapsedGroups.has('__orphans__')} timeout={160}>
            <Box>
              {bareOrphans.map(node => <NodeRow key={node.id} node={node} />)}
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
}

// ─── Timeline panel ──────────────────────────────────────────────────────────
function TimelinePanel({ allNodes, edges, setFocusId }) {
  const dated = allNodes.filter(n => ['activity', 'task', 'milestone', 'pivot'].includes(n.type) && n.target_date);
  const undated = allNodes.filter(n => ['activity', 'task', 'milestone', 'pivot'].includes(n.type) && !n.target_date);
  const nodeMap = Object.fromEntries(allNodes.map(n => [n.id, n]));

  const fmtDate = d => new Date(d + 'T00:00:00').toLocaleDateString('en-SG', { day: 'numeric', month: 'short' });

  // For each node, find the latest target_date among all predecessor activity nodes
  function getStartDate(node) {
    const incomingEdges = (edges || []).filter(e => e.target === node.id);
    const predecessors = incomingEdges.map(e => nodeMap[e.source]).filter(Boolean);
    const activityDates = predecessors
      .filter(p => p.type === 'activity' && p.target_date)
      .map(p => p.target_date);
    return activityDates.length > 0 ? activityDates.sort().at(-1) : null;
  }

  // Enrich nodes with start_date
  const enriched = dated.map(n => ({ ...n, start_date: getStartDate(n) }));

  const today = new Date(); today.setHours(0,0,0,0);
  const allD = enriched.flatMap(n => [new Date(n.target_date + 'T00:00:00'), n.start_date ? new Date(n.start_date + 'T00:00:00') : null].filter(Boolean));

  if (allD.length === 0 && dated.length === 0) return (
    <Box sx={{ bgcolor: 'var(--bg-surf)', borderTop: '1px solid var(--bd-lo)', p: 2 }}>
      <Typography sx={{ fontSize: '0.75rem', color: 'var(--tx-gh)' }}>No dated nodes yet.</Typography>
    </Box>
  );

  let minDate = new Date(Math.min(...allD, today));
  let maxDate = new Date(Math.max(...allD, today));
  minDate.setDate(1); minDate.setMonth(minDate.getMonth() - 1);
  maxDate.setDate(1); maxDate.setMonth(maxDate.getMonth() + 2);
  const range = maxDate - minDate || 1;
  const pct = d => ((new Date(d + 'T00:00:00') - minDate) / range) * 100;
  const todayPct = ((today - minDate) / range) * 100;

  // Build month markers
  const months = [];
  const cur = new Date(minDate);
  while (cur < maxDate) { months.push(new Date(cur)); cur.setMonth(cur.getMonth() + 1); }

  // Bin-pack into rows considering the full bar width
  const rows = [];
  const sorted = [...enriched].sort((a, b) => (a.start_date || a.target_date).localeCompare(b.start_date || b.target_date));
  sorted.forEach(node => {
    const startPct = node.start_date ? pct(node.start_date) : pct(node.target_date);
    const endPct = pct(node.target_date);
    const rowIdx = rows.findIndex(row => {
      const last = row[row.length - 1];
      const lastEnd = pct(last.target_date);
      return lastEnd + 2 < startPct;
    });
    if (rowIdx >= 0) rows[rowIdx].push(node);
    else rows.push([node]);
  });

  return (
    <Box sx={{ bgcolor: 'var(--bg-surf)', borderTop: '1px solid var(--bd-lo)', p: 2, overflowX: 'auto' }}>
      <Typography sx={{ fontSize: '0.68rem', color: 'var(--tx-dim)', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700, mb: 1.5 }}>
        Timeline · {dated.length} dated node{dated.length !== 1 ? 's' : ''}
        {undated.length > 0 && <span style={{ color: '#ef4444', marginLeft: 8 }}>{undated.length} missing date</span>}
      </Typography>

      <Box sx={{ position: 'relative', minWidth: 600 }}>
        {/* Month markers */}
        {months.map((m, i) => {
          const mp = ((m - minDate) / range) * 100;
          return (
            <Box key={i} sx={{ position: 'absolute', left: `${mp}%`, top: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', pointerEvents: 'none' }}>
              <Typography sx={{ fontSize: '0.58rem', color: 'var(--tx-gh)', whiteSpace: 'nowrap', transform: 'translateX(-2px)' }}>
                {m.toLocaleDateString('en-SG', { month: 'short', year: '2-digit' })}
              </Typography>
              <Box sx={{ width: '1px', flex: 1, bgcolor: 'var(--bd-lo)' }} />
            </Box>
          );
        })}

        {/* Today line */}
        {todayPct >= 0 && todayPct <= 100 && (
          <Box sx={{ position: 'absolute', left: `${todayPct}%`, top: 0, bottom: 0, zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none' }}>
            <Typography sx={{ fontSize: '0.55rem', color: '#6366f1', fontWeight: 700, whiteSpace: 'nowrap', transform: 'translateX(-50%)' }}>TODAY</Typography>
            <Box sx={{ width: '2px', flex: 1, bgcolor: '#6366f1', opacity: 0.7 }} />
          </Box>
        )}

        {/* Node rows */}
        <Box sx={{ mt: 1.5, pb: 0.5 }}>
          {rows.map((row, ri) => (
            <Box key={ri} sx={{ position: 'relative', height: 30, mb: 0.75 }}>
              {row.map(node => {
                const meta = NODE_META[node.type];
                const endPct = pct(node.target_date);
                const startPct = node.start_date ? pct(node.start_date) : null;
                const hasBar = startPct !== null && endPct - startPct > 0.5;
                const isPast = new Date(node.target_date + 'T00:00:00') < today && node.status !== 'done';
                const tooltip = `${meta.label}: ${node.title}\n${node.start_date ? fmtDate(node.start_date) + ' → ' : ''}${fmtDate(node.target_date)}`;
                return (
                  <Tooltip key={node.id} title={tooltip} placement="top">
                    <Box onClick={() => setFocusId(node.id)} sx={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', zIndex: 3, opacity: isPast ? 0.6 : 1, '&:hover': { zIndex: 10 } }}
                      style={{ left: `${hasBar ? startPct : endPct}%` }}
                    >
                      {/* Duration bar */}
                      {hasBar && (
                        <Box sx={{
                          position: 'absolute', top: '50%', transform: 'translateY(-50%)',
                          left: 0, width: `${(endPct - startPct) / 100 * 600}px`,
                          height: 8, borderRadius: 4,
                          bgcolor: `${meta.color}33`, border: `1px solid ${meta.color}55`,
                          minWidth: 4,
                        }}
                          style={{ width: `calc(${endPct - startPct}% * 6)` }}
                        />
                      )}
                      {/* End chip */}
                      <Box sx={{
                        position: 'absolute', top: '50%', transform: 'translate(-50%, -50%)',
                        left: hasBar ? `${endPct - startPct}%` : 0,
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        bgcolor: meta.bg, border: `1.5px solid ${meta.border}`,
                        borderRadius: 1.5, px: 0.75, py: 0.25, whiteSpace: 'nowrap',
                        '&:hover': { border: `1.5px solid ${meta.color}`, boxShadow: `0 0 8px ${meta.color}66` },
                        transition: 'all 0.15s',
                      }}>
                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: meta.color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.62rem', color: meta.color, fontWeight: 600, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {node.title}
                        </Typography>
                      </Box>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          ))}
        </Box>

        {/* Baseline */}
        <Box sx={{ height: '1px', bgcolor: 'var(--bd-lo)', width: '100%', mt: 0.5 }} />
      </Box>

      {undated.length > 0 && (
        <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          <Typography sx={{ fontSize: '0.62rem', color: '#ef4444', mr: 0.5, alignSelf: 'center' }}>No date:</Typography>
          {undated.map(n => (
            <Chip key={n.id} size="small" label={n.title}
              onClick={() => setFocusId(n.id)}
              sx={{ height: 18, fontSize: '0.6rem', cursor: 'pointer', bgcolor: `${NODE_META[n.type]?.bg}`, color: NODE_META[n.type]?.color, border: `1px solid ${NODE_META[n.type]?.border}` }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

// ─── Sidebar tree node (recursive) ───────────────────────────────────────────
function SidebarTreeNode({ node, depth = 0, allNodes, edges, focusId, setFocusId, expandedIds, toggleExpanded, setDeleteTarget }) {
  const meta = NODE_META[node.type] || NODE_META.task;
  const children = edges
    .filter(e => e.source === node.id)
    .map(e => allNodes.find(n => n.id === e.target))
    .filter(Boolean);
  const hasChildren = children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isActive = focusId === node.id;

  return (
    <>
      <Box
        onClick={() => setFocusId(node.id)}
        sx={{
          pl: 1.5 + depth * 1.5, pr: 1, py: depth === 0 ? 1.2 : 0.8,
          cursor: 'pointer',
          borderBottom: depth === 0 ? '1px solid var(--bg-low)' : 'none',
          bgcolor: isActive ? 'var(--bg-card)' : 'transparent',
          borderLeft: isActive ? `3px solid ${meta.color}` : depth === 0 ? '3px solid transparent' : `3px solid ${meta.color}22`,
          '&:hover': { bgcolor: 'var(--bg-hover)' },
          '&:hover .del-btn': { opacity: 1 },
          position: 'relative',
          transition: 'all 0.15s',
          display: 'flex', alignItems: 'center', gap: 0.5,
        }}
      >
        {/* Expand/collapse chevron */}
        <Box sx={{ width: 16, flexShrink: 0 }}>
          {hasChildren && (
            <IconButton size="small" onClick={e => { e.stopPropagation(); toggleExpanded(node.id); }}
              sx={{ width: 16, height: 16, p: 0, color: 'var(--tx-ft)', '&:hover': { color: 'var(--tx-md)' } }}
            >
              {isExpanded
                ? <ExpandMoreIcon sx={{ fontSize: 14 }} />
                : <ChevronRightIcon sx={{ fontSize: 14 }} />
              }
            </IconButton>
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.6} mb={depth === 0 ? 0.3 : 0.1}>
            <Box sx={{ width: depth === 0 ? 7 : 5, height: depth === 0 ? 7 : 5, borderRadius: '50%', bgcolor: meta.color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.6rem', color: meta.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>{meta.label}</Typography>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: STATUS_META[node.status]?.color, flexShrink: 0 }} title={node.status} />
          </Stack>
          <Typography sx={{ fontSize: depth === 0 ? '0.875rem' : '0.78rem', color: depth === 0 ? 'var(--tx-hi)' : 'var(--tx-md)', fontWeight: depth === 0 ? 700 : 500, lineHeight: 1.3, pr: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {node.title}
          </Typography>
        </Box>

        <IconButton className="del-btn" size="small"
          onClick={e => { e.stopPropagation(); setDeleteTarget(node.id); }}
          sx={{ position: 'absolute', top: '50%', right: 4, transform: 'translateY(-50%)', opacity: 0, color: 'var(--tx-ft)', transition: 'opacity 0.15s', '&:hover': { color: '#ef4444' }, p: 0.3, flexShrink: 0 }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 13 }} />
        </IconButton>
      </Box>

      {/* Children */}
      {hasChildren && (
        <Collapse in={isExpanded} timeout={180}>
          {children.map(child => (
            <SidebarTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              allNodes={allNodes}
              edges={edges}
              focusId={focusId}
              setFocusId={setFocusId}
              expandedIds={expandedIds}
              toggleExpanded={toggleExpanded}
              setDeleteTarget={setDeleteTarget}
            />
          ))}
        </Collapse>
      )}
    </>
  );
}

// ─── Main TrackerPage ─────────────────────────────────────────────────────────
export default function TrackerPage() {
  const isMobile = useMediaQuery('(max-width:600px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [allNodes, setAllNodes] = useState([]);   // flat index from KV
  const [edges, setEdges] = useState([]);          // all edges
  const [rfNodes, setRfNodes, onNodesChange] = useNodesState([]);
  const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [focusId, setFocusId] = useState(null);    // which initiative/opportunity we're viewing
  const [formOpen, setFormOpen] = useState(false);
  const [editNode, setEditNode] = useState(null);  // full node for editing
  const [formParentId, setFormParentId] = useState(null);
  const [formParentType, setFormParentType] = useState(null);
  const [formDefaultType, setFormDefaultType] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [undoStack, setUndoStack] = useState([]); // [{ node, edges }]
  const [undoSnack, setUndoSnack] = useState({ open: false, title: '' });
  const undoTimerRef = React.useRef(null);
  const resizeTimers = React.useRef({});
  const [connectMode, setConnectMode] = useState(false);
  const [timelineMode, setTimelineMode] = useState(false);
  const [listMode, setListMode] = useState(false);
  const [strategyModal, setStrategyModal] = useState(null); // { nodeId, nodeTitle, nodeType }
  const [strategyLoading, setStrategyLoading] = useState(false);
  const [strategyText, setStrategyText] = useState('');
  const [strategyDate, setStrategyDate] = useState('');
  const [strategySaving, setStrategySaving] = useState(false);
  const [wsInsightModal, setWsInsightModal] = useState(null); // { nodeId, nodeTitle }
  const [wsInsightTab, setWsInsightTab] = useState('summary'); // 'summary' | 'strategy'
  const [wsInsightSummary, setWsInsightSummary] = useState('');
  const [wsInsightStrategy, setWsInsightStrategy] = useState('');
  const [wsInsightLoading, setWsInsightLoading] = useState(false);
  const [jobsPanelOpen, setJobsPanelOpen] = useState(false);
  const [jobDetails, setJobDetails] = useState({}); // jobId → full detail from API
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [journalEntriesMap, setJournalEntriesMap] = useState({}); // nodeId → [{id, title, preview}]
  const { pendingJobs, addJob, removeJob } = useRecording();
  const [recordingFor, setRecordingFor] = useState(null); // { nodeId, nodeTitle }
  const [reviewJob, setReviewJob] = useState(null); // job object for review modal
  const [reviewContent, setReviewContent] = useState('');
  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewSaving, setReviewSaving] = useState(false);
  const toggleExpanded = (id) => setExpandedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const navigate = useNavigate();

  // Load all nodes + edges
  useEffect(() => { loadAll(); loadJournalCounts(); }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [nr, er] = await Promise.all([api.get('/api/tracker/nodes'), api.get('/api/tracker/edges')]);
      const nodes = nr.data.nodes || [];
      const edgesData = er.data.edges || [];
      setAllNodes(nodes);
      setEdges(edgesData);
      // Auto-focus first initiative or project
      const first = nodes.find(n => n.type === 'initiative') || nodes.find(n => n.type === 'project') || nodes[0];
      if (first) setFocusId(first.id);
    } catch {}
    finally { setLoading(false); }
  }

  async function deleteJournalEntry(entryId) {
    try {
      await api.delete(`/api/journal/${entryId}`);
      setJournalEntriesMap(prev => {
        const next = {};
        for (const [nodeId, entries] of Object.entries(prev)) {
          const filtered = entries.filter(e => e.id !== entryId);
          if (filtered.length > 0) next[nodeId] = filtered;
        }
        return next;
      });
    } catch {}
  }

  async function approveNode(id) {
    try {
      await api.put(`/api/tracker/nodes/${id}`, { status: 'open' });
      setAllNodes(prev => prev.map(n => n.id === id ? { ...n, status: 'open' } : n));
    } catch {}
  }

  async function rejectNode(id) {
    try {
      await api.delete(`/api/tracker/nodes/${id}`);
      setAllNodes(prev => prev.filter(n => n.id !== id));
      setEdges(prev => prev.filter(e => e.source !== id && e.target !== id));
    } catch {}
  }

  async function loadJournalCounts() {
    try {
      const res = await api.get('/api/journal');
      const entries = res.data.entries || [];
      const map = {};
      entries.forEach(e => {
        if (e.tracker_node_id) {
          if (!map[e.tracker_node_id]) map[e.tracker_node_id] = [];
          map[e.tracker_node_id].push({ id: e.id, title: e.title, preview: e.preview });
        }
      });
      setJournalEntriesMap(map);
    } catch {}
  }

  // Build ReactFlow graph whenever focus changes
  useEffect(() => {
    if (!focusId || allNodes.length === 0) { setRfNodes([]); setRfEdges([]); return; }
    buildGraph(focusId);
  }, [focusId, allNodes, edges, journalEntriesMap, pendingJobs, timelineMode]);

  function getDescendants(rootId) {
    // BFS to collect all nodes reachable from root via edges
    const visited = new Set([rootId]);
    const queue = [rootId];
    while (queue.length) {
      const cur = queue.shift();
      edges.filter(e => e.source === cur).forEach(e => {
        if (!visited.has(e.target)) { visited.add(e.target); queue.push(e.target); }
      });
    }
    return visited;
  }

  function buildGraph(rootId) {
    const inScope = getDescendants(rootId);
    // Also pull in direct parents of any in-scope node (multi-parent support)
    edges.forEach(e => { if (inScope.has(e.target) && !inScope.has(e.source)) inScope.add(e.source); });
    const scopeNodes = allNodes.filter(n => inScope.has(n.id));
    const scopeEdges = edges.filter(e => inScope.has(e.source) && inScope.has(e.target));

    const rfN = scopeNodes.map(n => {
      let first_activity_date = '';
      if (n.type === 'workstream') {
        const descIds = [...getDescendants(n.id)];
        descIds.shift(); // remove root (the workstream itself)
        const dates = descIds
          .map(id => allNodes.find(cn => cn.id === id))
          .filter(cn => cn && ['activity', 'task'].includes(cn.type) && cn.target_date)
          .map(cn => cn.target_date)
          .sort();
        if (dates.length) first_activity_date = dates[0];
      }
      return {
        id: n.id,
        type: 'trackerNode',
        position: { x: 0, y: 0 },
        style: { width: n.width || 220 },
        data: {
          id: n.id, type: n.type, sub_type: n.sub_type || '', label: n.title, status: n.status,
          description: n.description || '', goal: n.goal || '',
          target_date: n.target_date || '', date_started: n.date_started || '',
          first_activity_date,
          custom_fields: n.custom_fields || [],
          journalEntries: journalEntriesMap[n.id] || [],
          pendingJobs: pendingJobs.filter(j => j.tracker_node_id === n.id),
          onEdit: (id) => openEdit(id),
          onAddChild: (id, type) => openAddChild(id, type),
          onDelete: (id) => setDeleteTarget(id),
          onOpenJournal: (id, title, entryId) => navigate(`/journal?nodeId=${encodeURIComponent(id)}&nodeTitle=${encodeURIComponent(title)}${entryId ? `&entryId=${encodeURIComponent(entryId)}` : ''}`),
          onDeleteJournalEntry: (entryId) => deleteJournalEntry(entryId),
          onRecord: (id, title) => setRecordingFor({ nodeId: id, nodeTitle: title }),
          onRemoveJob: (jobId) => removeJob(jobId),
          onApprove: (id) => approveNode(id),
          onReject: (id) => rejectNode(id),
          onSuggestStrategy: (id, title, type, isExisting) => openStrategyModal(id, title, type, isExisting),
          onWorkstreamInsight: (id, title) => openWsInsight(id, title),
          onReviewJob: async (job) => {
            try {
              const res = await api.get(`/api/journal/${job.journal_id}`);
              const entry = res.data.entry;
              setReviewJob(job);
              setReviewContent(entry.content || '');
              setReviewTitle(entry.title || '');
            } catch {}
          },
        },
      };
    });

    const rfE = scopeEdges.map(e => {
      const em = EDGE_META[e.type] || EDGE_META.parent_of;
      return { id: e.id, source: e.source, target: e.target, ...em, type: 'smoothstep' };
    });

    // Build workstream groups for swim-lane layout
    const workstreams = scopeNodes.filter(n => n.type === 'workstream');
    const workstreamGroups = workstreams.length > 1
      ? workstreams.map(ws => {
          const descIds = getDescendants(ws.id);
          return {
            wsId: ws.id,
            wsLabel: ws.title,
            wsColor: NODE_META.workstream.color,
            wsStatus: ws.status,
            nodeIds: descIds,
          };
        })
      : null;

    const laid = getLayoutedElements(rfN, rfE, workstreamGroups);
    setRfNodes(timelineMode ? applyTimelineLayout(laid) : laid);
    setRfEdges(rfE);
  }

  async function openStrategyModal(nodeId, nodeTitle, nodeType, isExisting = false) {
    // For existing milestone/strategy nodes, find parent workstream/project for richer context
    let contextNodeId = nodeId;
    if (isExisting) {
      const parentEdge = edges.find(e => e.target === nodeId);
      if (parentEdge) {
        const parentNode = allNodes.find(n => n.id === parentEdge.source);
        if (parentNode && [...STRATEGY_TYPES, 'opportunity'].includes(parentNode.type)) {
          contextNodeId = parentNode.id;
        }
      }
    }
    setStrategyModal({ nodeId, nodeTitle, nodeType, isExisting, contextNodeId });
    setStrategyText('');
    setStrategyDate('');
    setStrategyLoading(true);
    try {
      const res = await api.post('/api/tracker/suggest-strategy', { node_id: contextNodeId });
      setStrategyText(res.data.strategy || '');
    } catch (e) {
      setStrategyText('Error generating strategy. You can type your strategy manually below.');
    } finally {
      setStrategyLoading(false);
    }
  }

  async function openWsInsight(nodeId, nodeTitle) {
    setWsInsightModal({ nodeId, nodeTitle });
    setWsInsightTab('summary');
    setWsInsightSummary('');
    setWsInsightStrategy('');
    setWsInsightLoading(true);
    try {
      const res = await api.post('/api/tracker/workstream-insight', { node_id: nodeId, mode: 'summary' });
      setWsInsightSummary(res.data.text || '');
    } catch { setWsInsightSummary('Error generating summary.'); }
    finally { setWsInsightLoading(false); }
  }

  async function loadWsInsightTab(tab) {
    setWsInsightTab(tab);
    if (!wsInsightModal) return;
    const alreadyLoaded = tab === 'summary' ? wsInsightSummary : wsInsightStrategy;
    if (alreadyLoaded) return;
    setWsInsightLoading(true);
    try {
      const res = await api.post('/api/tracker/workstream-insight', { node_id: wsInsightModal.nodeId, mode: tab });
      if (tab === 'summary') setWsInsightSummary(res.data.text || '');
      else setWsInsightStrategy(res.data.text || '');
    } catch {
      if (tab === 'summary') setWsInsightSummary('Error generating summary.');
      else setWsInsightStrategy('Error generating strategy suggestions.');
    } finally { setWsInsightLoading(false); }
  }

  async function saveStrategyMilestone() {
    if (!strategyModal || !strategyText.trim()) return;
    setStrategySaving(true);
    try {
      if (strategyModal.isExisting) {
        // Update the existing milestone/strategy node's description
        const res = await api.put(`/api/tracker/nodes/${strategyModal.nodeId}`, { description: strategyText.trim() });
        setAllNodes(prev => prev.map(n => n.id === strategyModal.nodeId ? { ...n, ...res.data.node } : n));
      } else {
        // Create a new milestone/strategy node and connect it
        const res = await api.post('/api/tracker/nodes', {
          type: 'milestone', sub_type: 'strategy',
          title: `Strategy — ${strategyModal.nodeTitle}`,
          status: 'open', description: strategyText.trim(),
          target_date: strategyDate || new Date().toISOString().split('T')[0],
        });
        const newNode = res.data.node;
        setAllNodes(prev => [...prev, newNode]);
        const er = await api.post('/api/tracker/edges', { source: strategyModal.nodeId, target: newNode.id, type: 'leads_to' });
        setEdges(prev => [...prev, er.data.edge]);
      }
      setStrategyModal(null);
    } catch (e) { /* silent */ }
    finally { setStrategySaving(false); }
  }

  async function openEdit(nodeId) {
    try {
      const res = await api.get(`/api/tracker/nodes/${nodeId}`);
      setEditNode(res.data.node);
      setFormParentId(null);
      setFormParentType(null);
      setFormDefaultType(null);
      setFormOpen(true);
      if (isMobile) setSidebarOpen(false);
    } catch {}
  }

  function openAddChild(parentId, parentType) {
    setEditNode(null);
    setFormParentId(parentId);
    setFormParentType(parentType);
    setFormDefaultType(ALLOWED_CHILDREN[parentType]?.[0] || 'activity');
    setFormOpen(true);
    if (isMobile) setSidebarOpen(false);
  }

  function openNewTopLevel() {
    setEditNode(null);
    setFormParentId(null);
    setFormParentType(null);
    setFormDefaultType('initiative');
    setFormOpen(true);
  }

  async function handleSave(form, existingId, connectToId, edgeType) {
    if (existingId) {
      // Update existing node
      const res = await api.put(`/api/tracker/nodes/${existingId}`, form);
      const updated = res.data.node;
      setAllNodes(prev => prev.map(n => n.id === existingId ? { ...n, ...updated } : n));
    } else {
      // Create new node
      const res = await api.post('/api/tracker/nodes', form);
      const newNode = res.data.node;
      setAllNodes(prev => [...prev, newNode]);
      // Create edge if connection selected
      if (connectToId) {
        const er = await api.post('/api/tracker/edges', { source: connectToId, target: newNode.id, type: edgeType });
        setEdges(prev => [...prev, er.data.edge]);
      }
      // If new initiative or top-level, focus it
      if (!connectToId || form.type === 'initiative') setFocusId(newNode.id);
    }
  }

  async function handleDelete(id) {
    // Fetch full node before deleting so we can restore it
    let fullNode = allNodes.find(n => n.id === id) || null;
    try { const r = await api.get(`/api/tracker/nodes/${id}`); fullNode = r.data.node; } catch {}
    const connectedEdges = edges.filter(e => e.source === id || e.target === id);

    await api.delete(`/api/tracker/nodes/${id}`);
    setAllNodes(prev => prev.filter(n => n.id !== id));
    setEdges(prev => prev.filter(e => e.source !== id && e.target !== id));
    if (focusId === id) {
      const remaining = allNodes.filter(n => n.id !== id && ROOT_TYPES.includes(n.type));
      setFocusId(remaining[0]?.id || null);
    }
    setDeleteTarget(null);

    // Save to undo stack and show snackbar
    if (fullNode) {
      setUndoStack(prev => [...prev, { node: fullNode, edges: connectedEdges }]);
      setUndoSnack({ open: true, title: fullNode.title });
      clearTimeout(undoTimerRef.current);
      undoTimerRef.current = setTimeout(() => {
        setUndoStack(prev => prev.slice(1)); // evict oldest entry after 10s
      }, 10000);
    }
  }

  async function handleUndo() {
    setUndoStack(prev => {
      if (prev.length === 0) return prev;
      const { node, edges: restoredEdges } = prev[prev.length - 1];
      // Restore node with original ID, then its edges
      api.post('/api/tracker/nodes', node).then(r => {
        const restoredNode = r.data.node;
        setAllNodes(a => [...a, restoredNode]);
        return Promise.all(restoredEdges.map(e => api.post('/api/tracker/edges', e)));
      }).then(results => {
        if (results) {
          const newEdges = results.map(r => r.data.edge);
          setEdges(a => [...a, ...newEdges]);
          setFocusId(node.id);
        }
      }).catch(() => {});
      return prev.slice(0, -1);
    });
    setUndoSnack({ open: false, title: '' });
    clearTimeout(undoTimerRef.current);
  }

  // Handle new edge drawn in ReactFlow
  const onConnect = useCallback(async (params) => {
    try {
      const er = await api.post('/api/tracker/edges', { source: params.source, target: params.target, type: 'leads_to' });
      setEdges(prev => [...prev, er.data.edge]);
      const em = EDGE_META.leads_to;
      setRfEdges(prev => addEdge({ ...params, ...em, id: er.data.edge.id, type: 'smoothstep' }, prev));
    } catch {}
  }, []);

  // Sidebar: show Initiative/Project always; show Opportunity/Workstream only if they have no parent
  const childIds = new Set(edges.map(e => e.target));
  const topLevel = allNodes.filter(n =>
    ['initiative', 'project'].includes(n.type) ||
    (['opportunity', 'workstream'].includes(n.type) && !childIds.has(n.id))
  );

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 52px)', bgcolor: 'var(--bg-base)', overflow: 'hidden' }}>

      {/* ── Left sidebar ──────────────────────────────────────────────────── */}
      {isMobile && sidebarOpen && (
        <Box onClick={() => setSidebarOpen(false)} sx={{ position: 'fixed', inset: 0, zIndex: 1199, bgcolor: 'rgba(0,0,0,0.5)' }} />
      )}
      <Box sx={{
        ...(isMobile ? {
          position: 'fixed', left: sidebarOpen ? 0 : -280, top: 52, bottom: 0, zIndex: 1200, transition: 'left 0.25s ease', width: 260,
        } : {
          width: sidebarOpen ? 260 : 0, minWidth: sidebarOpen ? 260 : 0, overflow: 'hidden',
          transition: 'width 0.22s ease, min-width 0.22s ease', flexShrink: 0,
          borderRight: sidebarOpen ? '1px solid var(--bd-lo)' : 'none',
        }),
        display: 'flex', flexDirection: 'column', bgcolor: 'var(--bg-surf)',
      }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid var(--bd-lo)', display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountTreeIcon sx={{ color: '#6366f1', fontSize: 18 }} />
          <Typography sx={{ fontWeight: 700, color: 'var(--tx-hi)', flex: 1, fontSize: '0.95rem' }}>Tracker</Typography>
          <Tooltip title="New Initiative / Project / Opportunity / Workstream">
            <IconButton size="small" onClick={openNewTopLevel} sx={{ bgcolor: '#6366f1', color: '#fff', width: 26, height: 26, '&:hover': { bgcolor: '#4f46e5' } }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton size="small" onClick={() => setSidebarOpen(false)} sx={{ color: 'var(--tx-dim)', '&:hover': { color: 'var(--tx-md)' }, width: 26, height: 26 }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Legend */}
        <Box sx={{ px: 2, py: 1, borderBottom: '1px solid var(--bg-low)' }}>
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {Object.entries(NODE_META).map(([k, v]) => (
              <Chip key={k} size="small" label={v.label}
                sx={{ height: 18, fontSize: '0.62rem', bgcolor: v.bg, color: v.color, border: `1px solid ${v.border}` }}
              />
            ))}
          </Stack>
        </Box>

        {/* Node list */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <Typography sx={{ color: 'var(--tx-gh)', fontSize: '0.8rem', p: 2 }}>Loading…</Typography>
          ) : topLevel.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography sx={{ color: 'var(--tx-gh)', fontSize: '0.8rem', mb: 1 }}>No nodes yet</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={openNewTopLevel} sx={{ color: '#6366f1', textTransform: 'none', fontSize: '0.8rem' }}>
                Create Initiative / Project
              </Button>
            </Box>
          ) : topLevel.map(n => (
            <SidebarTreeNode
              key={n.id}
              node={n}
              depth={0}
              allNodes={allNodes}
              edges={edges}
              focusId={focusId}
              setFocusId={setFocusId}
              expandedIds={expandedIds}
              toggleExpanded={toggleExpanded}
              setDeleteTarget={setDeleteTarget}
            />
          ))}
        </Box>

        {/* Edge type legend */}
        <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid var(--bd-lo)' }}>
          <Typography sx={{ fontSize: '0.65rem', color: 'var(--tx-gh)', mb: 0.8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Edge types</Typography>
          {Object.entries(EDGE_META).map(([k, v]) => (
            <Stack key={k} direction="row" alignItems="center" spacing={1} mb={0.4}>
              <Box sx={{ width: 24, height: 2, bgcolor: v.style.stroke, borderRadius: 1, flexShrink: 0,
                ...(v.style.strokeDasharray ? { backgroundImage: `repeating-linear-gradient(to right, ${v.style.stroke} 0, ${v.style.stroke} 4px, transparent 4px, transparent 8px)`, bgcolor: 'transparent', height: 1 } : {}) }} />
              <Typography sx={{ fontSize: '0.68rem', color: 'var(--tx-ft)' }}>{v.label}</Typography>
            </Stack>
          ))}
          <Typography sx={{ fontSize: '0.65rem', color: 'var(--tx-gh)', mt: 1 }}>Drag between node handles to connect</Typography>
        </Box>
      </Box>

      {/* ── Canvas ─────────────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
        {/* Graph area */}
        <Box sx={{ flex: 1, position: 'relative' }}>
          {isMobile && !sidebarOpen && (
            <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 10 }}>
              <IconButton size="small" onClick={() => setSidebarOpen(true)}
                sx={{ bgcolor: 'var(--bg-surf)', border: '1px solid var(--bd-lo)', color: 'var(--tx-md)', borderRadius: 1.5 }}>
                <MenuIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          {/* Toolbar: Timeline + Jobs */}
          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10, display: 'flex', gap: 0.75 }}>
            {/* Jobs panel toggle */}
            <Tooltip title="Recording jobs">
              <IconButton size="small" onClick={() => setJobsPanelOpen(o => !o)}
                sx={{ bgcolor: jobsPanelOpen ? '#6366f1' : 'var(--bg-surf)', border: `1px solid ${jobsPanelOpen ? '#6366f1' : 'var(--bd-lo)'}`, color: jobsPanelOpen ? '#fff' : 'var(--tx-md)', borderRadius: 1.5, position: 'relative' }}>
                <MicIcon fontSize="small" />
                {pendingJobs.filter(j => j.status === 'processing').length > 0 && (
                  <Box sx={{ position: 'absolute', top: 2, right: 2, width: 7, height: 7, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                )}
                {pendingJobs.filter(j => j.status === 'failed').length > 0 && pendingJobs.filter(j => j.status === 'processing').length === 0 && (
                  <Box sx={{ position: 'absolute', top: 2, right: 2, width: 7, height: 7, borderRadius: '50%', bgcolor: '#ef4444' }} />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title={timelineMode ? 'Exit timeline mode' : 'Timeline mode'}>
              <IconButton size="small" onClick={() => { setTimelineMode(o => !o); setListMode(false); }}
                sx={{ bgcolor: timelineMode ? '#6366f1' : 'var(--bg-surf)', border: `1px solid ${timelineMode ? '#6366f1' : 'var(--bd-lo)'}`, color: timelineMode ? '#fff' : 'var(--tx-md)', borderRadius: 1.5 }}>
                <CalendarMonthIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={listMode ? 'Exit list view' : 'List view'}>
              <IconButton size="small" onClick={() => { setListMode(o => !o); setTimelineMode(false); }}
                sx={{ bgcolor: listMode ? '#6366f1' : 'var(--bg-surf)', border: `1px solid ${listMode ? '#6366f1' : 'var(--bd-lo)'}`, color: listMode ? '#fff' : 'var(--tx-md)', borderRadius: 1.5 }}>
                <ViewListIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {listMode ? (
            <ListViewPanel
              allNodes={allNodes} edges={edges} focusId={focusId}
              journalEntriesMap={journalEntriesMap} navigate={navigate}
            />
          ) : focusId ? (
            <ReactFlow
              nodes={rfNodes}
              edges={rfEdges}
              onNodesChange={changes => {
                onNodesChange(changes);
                const dimChanges = changes.filter(c => c.type === 'dimensions' && c.dimensions?.width);
                for (const ch of dimChanges) {
                  clearTimeout(resizeTimers.current[ch.id]);
                  resizeTimers.current[ch.id] = setTimeout(() => {
                    api.put(`/api/tracker/nodes/${ch.id}`, { width: Math.round(ch.dimensions.width) }).catch(() => {});
                  }, 600);
                }
              }}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={{ padding: 0.3 }}
              style={{ background: 'var(--bg-base)', height: '100%' }}
              deleteKeyCode="Delete"
            >
              <Background color="var(--bg-card)" gap={24} size={1} />
              <Controls style={{ background: 'var(--bg-surf)', border: '1px solid var(--bd-lo)', borderRadius: 8 }} />
              {!isMobile && (
                <MiniMap
                  nodeColor={n => NODE_META[n.data?.type]?.color || 'var(--tx-dim)'}
                  style={{ background: 'var(--bg-surf)', border: '1px solid var(--bd-lo)', borderRadius: 8 }}
                />
              )}
            </ReactFlow>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 2 }}>
              <AccountTreeIcon sx={{ color: 'var(--bg-card)', fontSize: 64 }} />
              <Typography sx={{ color: 'var(--tx-gh)', fontSize: '1rem' }}>
                {allNodes.length === 0 ? 'Create your first Initiative or Project to get started' : 'Select a node from the sidebar'}
              </Typography>
              {allNodes.length === 0 && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={openNewTopLevel}
                  sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, textTransform: 'none', borderRadius: 2 }}
                >
                  New Initiative / Project
                </Button>
              )}
            </Box>
          )}
        </Box>

      </Box>

      {/* ── Recording jobs panel ──────────────────────────────────────────── */}
      <Drawer
        anchor="right" open={jobsPanelOpen} onClose={() => setJobsPanelOpen(false)}
        variant="persistent"
        PaperProps={{ sx: { width: 320, bgcolor: 'var(--bg-surf)', borderLeft: '1px solid var(--bd-lo)', display: 'flex', flexDirection: 'column' } }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid var(--bd-lo)', display: 'flex', alignItems: 'center', gap: 1 }}>
          <MicIcon sx={{ fontSize: 16, color: '#6366f1' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--tx-hi)', flex: 1 }}>Recording Jobs</Typography>
          <IconButton size="small" onClick={() => setJobsPanelOpen(false)} sx={{ color: 'var(--tx-dim)' }}><CloseIcon fontSize="small" /></IconButton>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
          {pendingJobs.length === 0 ? (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <MicIcon sx={{ fontSize: 32, color: 'var(--bg-card)', mb: 1 }} />
              <Typography sx={{ fontSize: '0.78rem', color: 'var(--tx-gh)' }}>No recording jobs yet</Typography>
            </Box>
          ) : [...pendingJobs].sort((a, b) => new Date(b.started_at || 0) - new Date(a.started_at || 0)).map(job => {
            const detail = jobDetails[job.job_id];
            const isExpanded = !!detail;
            return (
              <Box key={job.job_id} sx={{ mb: 1.5, bgcolor: 'var(--bg-low)', border: '1px solid var(--bd-lo)', borderRadius: 2, overflow: 'hidden' }}>
                {/* Job header */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1 }}>
                  {job.status === 'processing' && <HourglassEmptyIcon sx={{ fontSize: 14, color: '#f59e0b', flexShrink: 0 }} className="pulse-icon" />}
                  {job.status === 'done' && <CheckCircleIcon sx={{ fontSize: 14, color: '#10b981', flexShrink: 0 }} />}
                  {job.status === 'failed' && <ErrorIcon sx={{ fontSize: 14, color: '#ef4444', flexShrink: 0 }} />}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--tx-md)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {job.name}
                    </Typography>
                    {job.tracker_node_title && (
                      <Typography sx={{ fontSize: '0.65rem', color: '#818cf8' }}>{job.tracker_node_title}</Typography>
                    )}
                  </Box>
                  <Chip label={job.status} size="small"
                    sx={{ fontSize: '0.6rem', height: 18, fontWeight: 700, flexShrink: 0,
                      bgcolor: job.status === 'done' ? 'rgba(16,185,129,0.15)' : job.status === 'failed' ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)',
                      color: job.status === 'done' ? '#10b981' : job.status === 'failed' ? '#ef4444' : '#f59e0b',
                    }} />
                </Stack>

                {/* Progress bar for processing */}
                {job.status === 'processing' && <LinearProgress sx={{ height: 2, bgcolor: 'var(--bd-lo)', '& .MuiLinearProgress-bar': { bgcolor: '#f59e0b' } }} />}

                {/* Actions row */}
                <Stack direction="row" sx={{ px: 1.5, pb: 1, gap: 0.75 }}>
                  {/* Details toggle */}
                  <Button size="small" onClick={async () => {
                    if (detail) { setJobDetails(prev => { const n = {...prev}; delete n[job.job_id]; return n; }); return; }
                    try {
                      const res = await api.get(`/api/jobs/${job.job_id}`);
                      setJobDetails(prev => ({ ...prev, [job.job_id]: res.data }));
                    } catch (e) { setJobDetails(prev => ({ ...prev, [job.job_id]: { error: String(e) } })); }
                  }} sx={{ fontSize: '0.65rem', color: 'var(--tx-gh)', textTransform: 'none', px: 0.75, py: 0.3, minWidth: 0 }}>
                    {isExpanded ? 'Hide details' : 'Details'}
                  </Button>
                  {job.status === 'done' && (
                    <Button size="small" onClick={() => {
                      setJobsPanelOpen(false);
                      navigate(`/journal?nodeId=${encodeURIComponent(job.tracker_node_id || '')}&nodeTitle=${encodeURIComponent(job.tracker_node_title || '')}`);
                    }} sx={{ fontSize: '0.65rem', color: '#6366f1', textTransform: 'none', px: 0.75, py: 0.3, minWidth: 0 }}>
                      Open in Journal →
                    </Button>
                  )}
                  {job.status === 'failed' && (
                    <Button size="small" onClick={async () => {
                      try {
                        const res = await api.post('/api/transcribe-async', {
                          audio_key: detail?.job?.custom_fields?.find?.((f) => f.key === 'audio_file')?.value || job.audio_key,
                          recording_name: job.name,
                          tracker_node_id: job.tracker_node_id,
                          tracker_node_title: job.tracker_node_title,
                        });
                        addJob(res.data.job_id, job.name, job.tracker_node_id, job.tracker_node_title);
                        removeJob(job.job_id);
                      } catch {}
                    }} sx={{ fontSize: '0.65rem', color: '#f59e0b', textTransform: 'none', px: 0.75, py: 0.3, minWidth: 0 }}>
                      Retry
                    </Button>
                  )}
                  <Box sx={{ flex: 1 }} />
                  <IconButton size="small" onClick={() => removeJob(job.job_id)} sx={{ color: 'var(--tx-dim)', '&:hover': { color: '#ef4444' }, p: 0.3 }}>
                    <CloseIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Stack>

                {/* Expanded details */}
                {isExpanded && (
                  <Box sx={{ borderTop: '1px solid var(--bd-lo)', px: 1.5, py: 1 }}>
                    {detail.job?.error && (
                      <Box sx={{ mb: 1, p: 1, bgcolor: 'rgba(239,68,68,0.08)', borderRadius: 1, border: '1px solid rgba(239,68,68,0.2)' }}>
                        <Typography sx={{ fontSize: '0.65rem', color: '#ef4444', fontWeight: 600, mb: 0.25 }}>Error</Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: '#fca5a5', wordBreak: 'break-word' }}>{detail.job.error}</Typography>
                      </Box>
                    )}
                    {detail.debug && (
                      <Box>
                        <Typography sx={{ fontSize: '0.62rem', color: 'var(--tx-gh)', fontWeight: 600, mb: 0.5 }}>Deepgram callback preview</Typography>
                        <Box component="pre" sx={{ fontSize: '0.58rem', color: 'var(--tx-dim)', bgcolor: 'var(--bg-base)', p: 1, borderRadius: 1, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 120, overflowY: 'auto', m: 0 }}>
                          {detail.debug.slice(0, 600)}
                        </Box>
                      </Box>
                    )}
                    {!detail.job?.error && !detail.debug && (
                      <Typography sx={{ fontSize: '0.65rem', color: 'var(--tx-gh)' }}>No additional details available</Typography>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Drawer>

      {/* ── Node form drawer ───────────────────────────────────────────────── */}
      <NodeFormDrawer
        open={formOpen}
        onClose={() => setFormOpen(false)}
        node={editNode}
        parentId={formParentId}
        parentType={formParentType}
        defaultType={formDefaultType}
        allNodes={allNodes}
        edges={edges}
        onSave={handleSave}
        onDeleteNode={id => setDeleteTarget(id)}
        onAddEdge={async (sourceId, targetId, edgeType) => {
          const er = await api.post('/api/tracker/edges', { source: sourceId, target: targetId, type: edgeType });
          setEdges(prev => [...prev, er.data.edge]);
        }}
        onRemoveEdge={async (edgeId) => {
          await api.delete(`/api/tracker/edges/${edgeId}`);
          setEdges(prev => prev.filter(e => e.id !== edgeId));
          setRfEdges(prev => prev.filter(e => e.id !== edgeId));
        }}
      />

      {/* ── Delete confirm ─────────────────────────────────────────────────── */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        PaperProps={{ sx: { bgcolor: 'var(--bg-surf)', border: '1px solid var(--bd-lo)', borderRadius: 3 } }}
      >
        <DialogTitle sx={{ color: 'var(--tx-hi)', fontSize: '1rem' }}>Delete node?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--tx-lo)', fontSize: '0.875rem' }}>
            This will also remove all edges connected to this node. You can undo within 10 seconds after deletion.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ color: 'var(--tx-dim)', textTransform: 'none' }}>Cancel</Button>
          <Button onClick={() => handleDelete(deleteTarget)} variant="contained"
            sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' }, textTransform: 'none', borderRadius: 2 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Undo snackbar ──────────────────────────────────────────────────── */}
      <Snackbar
        open={undoSnack.open}
        autoHideDuration={10000}
        onClose={() => setUndoSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          icon={false}
          action={
            <Button size="small" onClick={handleUndo} sx={{ color: '#f59e0b', fontWeight: 700, textTransform: 'none', fontSize: '0.8rem' }}>
              Undo
            </Button>
          }
          sx={{ bgcolor: '#1c1a08', border: '1px solid #92400e', color: '#fcd34d', alignItems: 'center', '& .MuiAlert-action': { pt: 0 } }}
        >
          Deleted <strong>"{undoSnack.title}"</strong>
        </Alert>
      </Snackbar>

      {/* ── Recording modal ─────────────────────────────────────────────────── */}
      <RecordingModal
        open={!!recordingFor}
        onClose={() => setRecordingFor(null)}
        trackerNodeId={recordingFor?.nodeId}
        trackerNodeTitle={recordingFor?.nodeTitle}
        onJobStarted={(jobId, name, nodeId, nodeTitle) => addJob(jobId, name, nodeId, nodeTitle)}
      />

      {/* ── Review modal (inline on canvas) ────────────────────────────────── */}
      {/* ── Strategy suggestion modal ──────────────────────────────────────── */}
      <Dialog open={!!strategyModal} onClose={() => !strategyLoading && setStrategyModal(null)} maxWidth="md" fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--bg-surf)', border: '1px solid var(--bd-lo)', borderRadius: 3, height: '80vh', display: 'flex', flexDirection: 'column' } }}
      >
        {strategyModal && (<>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1, borderBottom: '1px solid var(--bd-lo)', flexShrink: 0 }}>
            <AutoAwesomeIcon sx={{ color: '#f97316', fontSize: 20 }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--tx-hi)' }}>Strategy Suggestion</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#f97316' }}>{strategyModal.nodeTitle}</Typography>
            </Box>
            <IconButton size="small" onClick={() => setStrategyModal(null)} disabled={strategyLoading} sx={{ color: 'var(--tx-dim)' }}><CloseIcon fontSize="small" /></IconButton>
          </DialogTitle>
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 2, gap: 1.5 }}>
            {strategyLoading ? (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                <LinearProgress sx={{ width: '60%', borderRadius: 1 }} />
                <Typography sx={{ fontSize: '0.8rem', color: 'var(--tx-gh)' }}>Analysing activities, learnings and notes…</Typography>
              </Box>
            ) : (
              <>
                <Typography sx={{ fontSize: '0.72rem', color: 'var(--tx-gh)' }}>
                  AI-drafted strategy below. Edit freely before creating the milestone.
                </Typography>
                <textarea
                  value={strategyText}
                  onChange={e => setStrategyText(e.target.value)}
                  style={{ flex: 1, width: '100%', padding: '12px 16px', background: 'var(--bg-write)', color: 'var(--tx-write)',
                    border: '1px solid var(--bd-lo)', outline: 'none', resize: 'none', borderRadius: 8,
                    fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace", fontSize: '0.85rem', lineHeight: 1.7, boxSizing: 'border-box' }}
                />
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Typography sx={{ fontSize: '0.75rem', color: 'var(--tx-dim)', flexShrink: 0 }}>Milestone date</Typography>
                  <input type="date" value={strategyDate} onChange={e => setStrategyDate(e.target.value)}
                    style={{ background: 'var(--bg-card)', color: 'var(--tx-md)', border: '1px solid var(--tx-gh)', borderRadius: 6, padding: '4px 8px', fontSize: '0.8rem', colorScheme: 'dark' }} />
                </Stack>
              </>
            )}
          </Box>
          <DialogActions sx={{ px: 2.5, pb: 2, borderTop: '1px solid var(--bd-lo)', flexShrink: 0 }}>
            <Button onClick={() => setStrategyModal(null)} disabled={strategyLoading} sx={{ color: 'var(--tx-dim)', textTransform: 'none' }}>Cancel</Button>
            <Button variant="contained" disabled={strategyLoading || strategySaving || !strategyText.trim()}
              onClick={saveStrategyMilestone}
              startIcon={<AutoAwesomeIcon sx={{ fontSize: 15 }} />}
              sx={{ bgcolor: '#f97316', '&:hover': { bgcolor: '#ea6c07' }, textTransform: 'none', borderRadius: 2 }}
            >
              {strategySaving ? (strategyModal?.isExisting ? 'Saving…' : 'Creating…') : strategyModal?.isExisting ? 'Save to Node' : 'Create Strategy Milestone'}
            </Button>
          </DialogActions>
        </>)}
      </Dialog>

      {/* ── Workstream AI Insight modal ─────────────────────────────────────── */}
      <Dialog open={!!wsInsightModal} onClose={() => !wsInsightLoading && setWsInsightModal(null)} maxWidth="md" fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--bg-surf)', border: '1px solid var(--bd-lo)', borderRadius: 3, height: '80vh', display: 'flex', flexDirection: 'column' } }}
      >
        {wsInsightModal && (<>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1, borderBottom: '1px solid var(--bd-lo)', flexShrink: 0 }}>
            <AutoAwesomeIcon sx={{ color: '#818cf8', fontSize: 20 }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--tx-hi)' }}>AI Insights</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#818cf8' }}>{wsInsightModal.nodeTitle}</Typography>
            </Box>
            <IconButton size="small" onClick={() => setWsInsightModal(null)} disabled={wsInsightLoading} sx={{ color: 'var(--tx-dim)' }}><CloseIcon fontSize="small" /></IconButton>
          </DialogTitle>
          {/* Tab bar */}
          <Stack direction="row" sx={{ borderBottom: '1px solid var(--bd-lo)', flexShrink: 0 }}>
            {[{ id: 'summary', label: 'Summary' }, { id: 'strategy', label: 'Strategy' }].map(t => (
              <Button key={t.id} onClick={() => loadWsInsightTab(t.id)} disabled={wsInsightLoading}
                sx={{ flex: 1, borderRadius: 0, py: 1.25, textTransform: 'none', fontWeight: wsInsightTab === t.id ? 700 : 400,
                  color: wsInsightTab === t.id ? '#818cf8' : 'var(--tx-dim)',
                  borderBottom: wsInsightTab === t.id ? '2px solid #818cf8' : '2px solid transparent',
                  '&:hover': { bgcolor: 'var(--bg-hover)' } }}>
                {t.label}
              </Button>
            ))}
          </Stack>
          <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
            {wsInsightLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                <LinearProgress sx={{ width: '60%', borderRadius: 1 }} />
                <Typography sx={{ fontSize: '0.8rem', color: 'var(--tx-gh)' }}>
                  {wsInsightTab === 'summary' ? 'Summarising workstream…' : 'Generating strategy suggestions…'}
                </Typography>
              </Box>
            ) : (
              <Box sx={aiMdSx}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {wsInsightTab === 'summary' ? wsInsightSummary : wsInsightStrategy}
                </ReactMarkdown>
              </Box>
            )}
          </Box>
          <DialogActions sx={{ px: 2.5, pb: 2, borderTop: '1px solid var(--bd-lo)', flexShrink: 0 }}>
            <Button onClick={() => setWsInsightModal(null)} sx={{ color: 'var(--tx-dim)', textTransform: 'none' }}>Close</Button>
            <Button variant="outlined" disabled={wsInsightLoading}
              onClick={() => {
                const text = wsInsightTab === 'summary' ? wsInsightSummary : wsInsightStrategy;
                if (wsInsightTab === 'summary') setWsInsightSummary('');
                else setWsInsightStrategy('');
                loadWsInsightTab(wsInsightTab);
              }}
              startIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
              sx={{ borderColor: '#818cf8', color: '#818cf8', textTransform: 'none', borderRadius: 2, '&:hover': { borderColor: '#a5b4fc', bgcolor: 'rgba(129,140,248,0.1)' } }}
            >Regenerate</Button>
          </DialogActions>
        </>)}
      </Dialog>

      <Dialog open={!!reviewJob} onClose={() => setReviewJob(null)} maxWidth="md" fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--bg-surf)', border: '1px solid var(--bd-lo)', borderRadius: 3, height: '80vh', display: 'flex', flexDirection: 'column' } }}
      >
        {reviewJob && (<>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1, borderBottom: '1px solid var(--bd-lo)', flexShrink: 0 }}>
            <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--tx-hi)' }}>{reviewJob.title || reviewJob.body}</Typography>
              {reviewJob.tracker_node_title && (
                <Typography sx={{ fontSize: '0.72rem', color: '#818cf8' }}>{reviewJob.tracker_node_title}</Typography>
              )}
            </Box>
            <IconButton size="small" onClick={() => setReviewJob(null)} sx={{ color: 'var(--tx-dim)' }}><CloseIcon fontSize="small" /></IconButton>
          </DialogTitle>
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 2 }}>
            <Typography sx={{ fontSize: '0.72rem', color: 'var(--tx-gh)', mb: 1 }}>
              Review and edit the meeting summary below, then save to finalise.
            </Typography>
            <textarea
              value={reviewContent}
              onChange={e => setReviewContent(e.target.value)}
              style={{ flex: 1, width: '100%', padding: '12px 16px', background: 'var(--bg-write)', color: 'var(--tx-write)',
                border: '1px solid var(--bd-lo)', outline: 'none', resize: 'none', borderRadius: 8,
                fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace", fontSize: '0.88rem', lineHeight: 1.7, boxSizing: 'border-box' }}
            />
          </Box>
          <DialogActions sx={{ px: 2.5, pb: 2, borderTop: '1px solid var(--bd-lo)', flexShrink: 0 }}>
            <Button onClick={() => setReviewJob(null)} sx={{ color: 'var(--tx-dim)', textTransform: 'none' }}>Close</Button>
            <Button variant="contained" disabled={reviewSaving}
              onClick={async () => {
                setReviewSaving(true);
                try {
                  await api.put(`/api/journal/${reviewJob.journal_id}`, { title: reviewTitle, content: reviewContent });
                  setReviewJob(null);
                  loadJournalCounts();
                } catch {}
                finally { setReviewSaving(false); }
              }}
              sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, textTransform: 'none', borderRadius: 2 }}
            >
              {reviewSaving ? 'Saving…' : 'Save & Close'}
            </Button>
          </DialogActions>
        </>)}
      </Dialog>
    </Box>
  );
}
